<template>
  <div class="repair-slip-form main row">
    <div class="col-md-12">
      <KTCodePreview v-bind:title="pageTitle">
        <template v-slot:toolbar>
          <div class="d-flex align-items-center flex-nowrap toolbar-actions">
            <!-- Cụm trạng thái + Bill IDs (chỉ hiển thị, không action) -->
            <b-badge
              v-if="isEdit && form.status"
              :variant="statusVariant(form.status)"
              class="toolbar-badge mr-2"
            >
              {{ statusLabel(form.status) }}
            </b-badge>
            <!-- Gom HĐ BL + HĐ BH + HĐ Hoàn tiền vào 1 dropdown "HĐ liên kết" cho gọn toolbar -->
            <b-dropdown
              v-if="isEdit && hasLinkedBills"
              variant="info"
              size="sm"
              right
              class="mr-2"
            >
              <template #button-content>
                <i class="fas fa-link mr-1"></i>HĐ liên kết
              </template>
              <!-- M7 V2: HĐ BÁN LẺ (auto-tạo khi returned) -->
              <b-dropdown-item
                v-if="form.serviceBillId"
                @click="openLinkedBill('service')"
              >
                <i class="fas fa-file-invoice text-dark mr-2"></i>
                HĐ Bán lẻ
                <span v-if="form.serviceBillNumber" class="text-muted ml-1">
                  #{{ form.serviceBillNumber }}
                </span>
              </b-dropdown-item>
              <!-- M11 V2: HĐ BẢO HÀNH (link từ module warranty-bill) -->
              <b-dropdown-item
                v-if="form.warrantyBillId"
                @click="openLinkedBill('warranty')"
              >
                <i class="fas fa-shield-alt text-info mr-2"></i>
                HĐ Bảo hành
                <span v-if="form.warrantyBillNumber" class="text-muted ml-1">
                  #{{ form.warrantyBillNumber }}
                </span>
              </b-dropdown-item>
              <!-- M8: HĐ Hoàn tiền -->
              <b-dropdown-item
                v-if="form.refundBillId"
                @click="openLinkedBill('refund')"
              >
                <i class="fas fa-money-bill-wave text-danger mr-2"></i>
                HĐ Hoàn tiền
                <span v-if="form.refundBillNumber" class="text-muted ml-1">
                  #{{ form.refundBillNumber }}
                </span>
              </b-dropdown-item>
            </b-dropdown>

            <!-- Action chính: Chuyển trạng thái -->
            <b-dropdown
              v-if="isEdit && canChangeStatus && allowedNextStatuses.length > 0"
              variant="success"
              size="sm"
              right
              class="mr-2"
            >
              <template #button-content>
                <i class="fas fa-exchange-alt mr-1"></i>Chuyển trạng thái
              </template>
              <b-dropdown-item
                v-for="s in allowedNextStatuses"
                :key="s"
                @click="openTransitionModal(s)"
              >
                <b-badge :variant="statusVariant(s)" class="mr-1">{{ statusLabel(s) }}</b-badge>
              </b-dropdown-item>
            </b-dropdown>

            <!-- Gom các tác vụ phụ vào 1 dropdown để toolbar gọn 1 hàng -->
            <b-dropdown
              v-if="isEdit"
              variant="outline-secondary"
              size="sm"
              right
              class="mr-2"
              text="Tác vụ"
            >
              <template #button-content>
                <i class="fas fa-ellipsis-h mr-1"></i>Tác vụ
              </template>
              <b-dropdown-item @click="openPrintReceipt">
                <i class="fas fa-print text-info mr-2"></i>In biên nhận
              </b-dropdown-item>
              <b-dropdown-item
                v-if="form.serviceBillId"
                @click="openPrintBill"
              >
                <i class="fas fa-print text-info mr-2"></i>In hoá đơn dịch vụ
              </b-dropdown-item>
              <b-dropdown-divider v-if="canShowUndoReturn || canShowRefund"></b-dropdown-divider>
              <b-dropdown-item
                v-if="canShowUndoReturn"
                :disabled="!canUndoReturn"
                v-b-tooltip.hover.left
                :title="undoReturnDisabledReason"
                @click="openUndoReturnModal"
              >
                <i class="fas fa-undo text-warning mr-2"></i>Hoàn tác trả máy
              </b-dropdown-item>
              <b-dropdown-item
                v-if="canShowRefund"
                :disabled="!canRefund"
                v-b-tooltip.hover.left
                :title="refundDisabledReason"
                @click="openRefundModal"
              >
                <i class="fas fa-money-bill-wave text-danger mr-2"></i>Hoàn tiền cho khách
              </b-dropdown-item>
            </b-dropdown>

            <b-button
              variant="secondary"
              size="sm"
              class="mr-2"
              @click="goBack"
            >
              <i class="fas fa-arrow-left mr-1"></i>Quay lại
            </b-button>
            <b-button
              v-if="canSubmit"
              variant="primary"
              size="sm"
              :disabled="submitting"
              @click="onSubmit"
            >
              <i class="fas fa-save mr-1"></i>
              {{ submitting ? 'Đang lưu...' : (isEdit ? 'Cập nhật' : 'Tạo phiếu') }}
            </b-button>
          </div>
        </template>

        <template v-slot:preview>
          <!-- Banner phí chẩn đoán: hiện khi phiếu kết thúc ở BER / KH từ chối báo giá có ghi nhận phí.
               Chỉ là cờ nhắc KTBH thu KH + tạo/sửa HĐ bán lẻ tay với mục "Phí chẩn đoán" (Decision 1.29 —
               HĐ bán lẻ chỉ auto-tạo ở Hoàn thành; REJECTED_QUOTE/BER không tự tạo Bill). -->
          <b-alert
            v-if="showDiagnosisChargeBanner"
            variant="warning"
            show
            class="mb-3 py-2"
          >
            <i class="fas fa-exclamation-triangle mr-1"></i>
            <strong>Phí chẩn đoán đã ghi nhận: {{ formatPrice(form.diagnosisCharge) }} đ</strong>
            — KTBH thu KH số tiền này và tạo/sửa Hoá đơn bán lẻ tay với mục "Phí chẩn đoán"
            để vào sổ (phiếu ở trạng thái này không tự tạo HĐ).
          </b-alert>
          <!-- Banner yêu cầu hoàn tiền: chỉ là metadata trên phiếu, không phải hoàn tiền thật.
               KTBH phải tạo HĐ trả hàng từ Bill bán lẻ gốc để hoàn tiền thực cho KH. -->
          <div v-if="refundRecord" class="refund-banner mb-3">
            <div class="refund-banner__icon">
              <i class="fas fa-undo-alt"></i>
            </div>
            <div class="refund-banner__content">
              <div class="refund-banner__title">
                Đã ghi nhận yêu cầu hoàn tiền
                <strong class="ml-1">{{ formatPrice(refundRecord.amount) }} đ</strong>
                <span v-if="refundRecord.reasonLabel" class="refund-banner__reason">
                  · {{ refundRecord.reasonLabel }}
                </span>
              </div>
              <div v-if="refundRecord.note" class="refund-banner__note">
                <i class="fas fa-sticky-note mr-1"></i>{{ refundRecord.note }}
              </div>
              <div class="refund-banner__hint">
                Đây chỉ là cờ theo dõi. Để hoàn tiền thật cho KH, KTBH mở
                <strong>Hoá đơn bán lẻ gốc</strong> (badge "HĐ dịch vụ" ở trên) →
                bấm <strong>"Tạo HĐ trả hàng"</strong> → đi luồng trả hàng chuẩn.
              </div>
            </div>
          </div>
          <!-- Wizard pattern — đúng style UpsertOrder (compact, no number/desc) -->
          <div
            class="wizard wizard-4"
            id="kt_wizard_repair_slip"
            data-wizard-state="step-first"
            data-wizard-clickable="true"
          >
            <div class="wizard-nav wizard-nav-bg" style="border-radius: 0px">
              <div class="wizard-steps">
                <div
                  class="wizard-step mb-0"
                  data-wizard-type="step"
                  data-wizard-state="current"
                >
                  <div class="wizard-wrapper p-2">
                    <div class="wizard-label">
                      <div class="wizard-title">1. Khách hàng &amp; Thiết bị</div>
                    </div>
                  </div>
                </div>
                <div class="wizard-step ml-1 mb-0" data-wizard-type="step">
                  <div class="wizard-wrapper p-2">
                    <div class="wizard-label">
                      <div class="wizard-title">2. Mã lỗi &amp; Dịch vụ</div>
                    </div>
                  </div>
                </div>
                <div class="wizard-step ml-1 mb-0" data-wizard-type="step">
                  <div class="wizard-wrapper p-2">
                    <div class="wizard-label">
                      <div class="wizard-title">3. Nhân sự &amp; Ghi chú</div>
                    </div>
                  </div>
                </div>
                <div
                  v-if="isEdit"
                  class="wizard-step ml-1 mb-0"
                  data-wizard-type="step"
                >
                  <div class="wizard-wrapper p-2">
                    <div class="wizard-label">
                      <div class="wizard-title">4. Tài liệu</div>
                    </div>
                  </div>
                </div>
                <div
                  v-if="isEdit"
                  class="wizard-step ml-1 mb-0"
                  data-wizard-type="step"
                >
                  <div class="wizard-wrapper p-2">
                    <div class="wizard-label">
                      <div class="wizard-title">5. Lịch sử</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="card card-custom card-shadowless rounded-top-0">
              <div class="card-body p-0">
                <div class="row py-3">
                  <div class="col-xl-12">

            <!-- ============== STEP 1: Khách hàng & Thiết bị ============== -->
            <div
              class="pb-5"
              data-wizard-type="step-content"
              data-wizard-state="current"
            >
              <!-- Section: Thông tin phiếu -->
              <b-card no-body class="mb-4">
                <b-card-header class="py-2 bg-light">
                  <strong class="text-primary">Thông tin phiếu</strong>
                </b-card-header>
                <b-card-body>
                  <b-row>
                    <b-col md="4">
                      <b-form-group>
                        <label>Cửa hàng <span class="text-danger">*</span></label>
                        <AutoSuggest
                          :model="storeSearchText"
                          :suggestions="filteredStoreOptions"
                          suggestionName="displayName"
                          placeholder="cửa hàng (gõ tên hoặc mã)"
                          :limit="50"
                          :disabled="isEdit"
                          @change="onSearchStore"
                          @select="onSelectStore"
                        />
                        <small
                          v-if="$v.form.storeId.$invalid && $v.form.storeId.$dirty"
                          class="text-danger"
                        >
                          Bắt buộc chọn cửa hàng.
                        </small>
                      </b-form-group>
                    </b-col>
                    <b-col md="4">
                      <b-form-group>
                        <label>Nguồn khách hàng</label>
                        <b-form-select
                          v-model="form.orderSource"
                          :options="orderSourceOptions"
                          size="sm"
                          :disabled="readOnly"
                        ></b-form-select>
                      </b-form-group>
                    </b-col>
                    <b-col md="4">
                      <b-form-group>
                        <label>Loại hàng</label>
                        <b-form-radio-group
                          v-model="form.isExternalDevice"
                          size="sm"
                          disabled
                        >
                          <b-form-radio :value="false">Hàng DDV</b-form-radio>
                          <b-form-radio :value="true">Hàng ngoài</b-form-radio>
                        </b-form-radio-group>
                        <small class="text-muted">
                          <template v-if="isDirectCreate">
                            Tạo trực tiếp chỉ dành cho <strong>Hàng ngoài</strong> —
                            KH mang vào, IMEI/Serial nhập tay. Hàng DDV phải tạo từ HĐ bảo hành.
                          </template>
                          <template v-else-if="!isEdit">
                            Tạo từ HĐ bảo hành → <strong>Hàng DDV</strong>, data đã pre-fill.
                          </template>
                          <template v-else>
                            <i class="fas fa-lock"></i>
                            {{ form.isExternalDevice
                              ? 'KH mang vào, không validate BH.'
                              : 'Đã bán bởi DDV, có IMEI trong DB.' }}
                            Loại hàng cố định từ lúc tạo phiếu, không đổi được.
                          </template>
                        </small>
                      </b-form-group>
                    </b-col>
                  </b-row>
                </b-card-body>
              </b-card>

              <!-- Section: Khách hàng -->
              <b-card no-body class="mb-4">
                <b-card-header class="py-2 bg-light">
                  <strong class="text-primary">Khách hàng</strong>
                </b-card-header>
                <b-card-body>
                  <b-row>
                    <b-col md="3">
                      <b-form-group>
                        <label>
                          SĐT <span class="text-danger">*</span>
                          <small v-if="form.customerId" class="text-success ml-1">
                            <i class="fas fa-user-check"></i> KH cũ
                          </small>
                        </label>
                        <AutoSuggest
                          :model="form.customerPhone"
                          :suggestions="customerOptions"
                          suggestionName="phoneNo"
                          placeholder="số điện thoại"
                          :limit="10"
                          :disabled="readOnly"
                          @select="onSelectCustomer"
                          @change="onSearchCustomer"
                        >
                          <template #custom="{ suggestion }">
                            <div class="d-flex flex-column">
                              <span class="text-primary mb-1" v-if="suggestion.item.phoneNo">
                                ({{ suggestion.item.phoneNo }})
                              </span>
                              <span v-if="suggestion.item.fullName">{{ suggestion.item.fullName }}</span>
                              <span v-else class="text-muted font-italic">{{ suggestion.item.message }}</span>
                            </div>
                          </template>
                        </AutoSuggest>
                        <small v-if="$v.form.customerPhone.$invalid && $v.form.customerPhone.$dirty" class="text-danger">
                          Bắt buộc, max 20 ký tự.
                        </small>
                      </b-form-group>
                    </b-col>
                    <b-col md="6">
                      <b-form-group>
                        <label>Tên khách hàng <span class="text-danger">*</span></label>
                        <b-form-input
                          v-model.trim="$v.form.customerName.$model"
                          :state="validateState('customerName')"
                          size="sm"
                          :disabled="readOnly"
                          :placeholder="form.customerId ? 'Auto-fill từ KH cũ' : 'Tên KH mới'"
                        ></b-form-input>
                        <b-form-invalid-feedback>Bắt buộc, max 255 ký tự.</b-form-invalid-feedback>
                      </b-form-group>
                    </b-col>
                    <b-col md="3">
                      <b-form-group>
                        <label>Email</label>
                        <b-form-input
                          v-model.trim="form.customerEmail"
                          size="sm"
                          type="email"
                          :disabled="readOnly"
                        ></b-form-input>
                      </b-form-group>
                    </b-col>
                  </b-row>
                  <b-row class="address-row">
                    <b-col md="3">
                      <b-form-group>
                        <label>Tỉnh / TP</label>
                        <treeselect
                          :options="cityOptions"
                          :multiple="false"
                          :match-keys="['label', 'custom']"
                          :disabled="readOnly"
                          placeholder="Chọn TP"
                          noResultsText="Không có kết quả"
                          openDirection="bottom"
                          v-model="form.customerCityId"
                        />
                      </b-form-group>
                    </b-col>
                    <b-col md="3">
                      <b-form-group>
                        <label>Phường / Xã</label>
                        <treeselect
                          :options="wardOptions"
                          :multiple="false"
                          :match-keys="['label', 'custom']"
                          :disabled="readOnly || !form.customerCityId"
                          placeholder="Chọn Phường/Xã"
                          noResultsText="Không có kết quả"
                          openDirection="bottom"
                          v-model="form.customerWardId"
                        />
                      </b-form-group>
                    </b-col>
                    <b-col md="3">
                      <b-form-group>
                        <label>Địa chỉ</label>
                        <b-form-input
                          v-model.trim="form.customerAddress"
                          size="sm"
                          :disabled="readOnly"
                          placeholder="Số nhà, tên đường…"
                        ></b-form-input>
                      </b-form-group>
                    </b-col>
                    <b-col md="3">
                      <b-form-group>
                        <label>Mật khẩu máy</label>
                        <b-form-radio-group
                          v-model="form.machinePasswordType"
                          :options="passwordTypeOptions"
                          size="sm"
                          class="mb-1"
                          :disabled="readOnly || form.machinePasswordMasked"
                        ></b-form-radio-group>
                        <b-form-input
                          v-model.trim="form.machinePassword"
                          size="sm"
                          :placeholder="passwordPlaceholder"
                          :disabled="readOnly || form.machinePasswordMasked"
                        ></b-form-input>
                        <small v-if="form.machinePasswordMasked" class="text-muted">
                          Chỉ KTV phụ trách phiếu được xem/sửa.
                        </small>
                        <small v-else-if="form.machinePasswordType === 'pattern'" class="text-muted">
                          Mô tả hình vẽ — VD: "L từ góc trên trái" / "1→2→5→8→9".
                        </small>
                      </b-form-group>
                    </b-col>
                  </b-row>
                </b-card-body>
              </b-card>

              <!-- Section: Thiết bị -->
              <b-card no-body class="mb-4">
                <b-card-header class="py-2 bg-light">
                  <strong class="text-primary">Thiết bị</strong>
                </b-card-header>
                <b-card-body>
                  <b-row>
                    <b-col md="5">
                      <b-form-group>
                        <label>
                          Tên sản phẩm
                          <span class="text-danger">*</span>
                          <small v-if="form.productId" class="text-success ml-1">
                            <i class="fas fa-check"></i> Đã liên kết SP
                          </small>
                        </label>
                        <AutoSuggest
                          :model="searchProductText"
                          :suggestions="productOptions"
                          placeholder="tên / mã / barcode sản phẩm (productType=2)"
                          :limit="20"
                          :disabled="readOnly || isWarrantyLinked"
                          suggestionName="productName"
                          @change="onInputProduct"
                          @select="onSelectProduct"
                        >
                          <template #custom="{ suggestion }">
                            <div>
                              <div>{{ suggestion.item.productName }}</div>
                              <small class="text-muted">
                                {{ suggestion.item.productCode }}
                                <span v-if="suggestion.item.barCode"> · {{ suggestion.item.barCode }}</span>
                              </small>
                            </div>
                          </template>
                        </AutoSuggest>
                        <small v-if="!form.productId && !isFromWarrantyBill" class="text-danger">
                          Bắt buộc chọn sản phẩm từ danh mục — không gõ tự do.
                          Chưa có? <strong>Vào menu Sản phẩm để tạo mới trước.</strong>
                        </small>
                        <small v-if="isWarrantyLinked" class="text-info d-block mt-1">
                          <i class="fas fa-lock"></i>
                          Máy lấy từ HĐ bảo hành liên kết — không đổi sang SP ngoài HĐ.
                        </small>
                      </b-form-group>
                    </b-col>
                    <b-col md="2">
                      <b-form-group>
                        <label>Hãng</label>
                        <AutoSuggest
                          :model="brandSearchText"
                          :suggestions="filteredBrandOptions"
                          suggestionName="name"
                          placeholder="hãng (gõ tên)"
                          :limit="20"
                          :disabled="readOnly || isWarrantyLinked"
                          @change="onSearchBrand"
                          @select="onSelectBrand"
                        />
                        <small v-if="!form.brandName && form.productId" class="text-muted">
                          SP chưa gán hãng — gõ tìm rồi chọn.
                        </small>
                      </b-form-group>
                    </b-col>
                    <b-col md="2">
                      <b-form-group>
                        <label>Màu</label>
                        <b-form-input
                          v-model.trim="form.color"
                          size="sm"
                          :disabled="readOnly || isWarrantyLinked"
                        ></b-form-input>
                      </b-form-group>
                    </b-col>
                    <b-col md="3">
                      <b-form-group>
                        <label>
                          IMEI / Serial
                          <span class="text-danger">*</span>
                        </label>
                        <b-form-input
                          v-model.trim="form.imei"
                          size="sm"
                          maxlength="20"
                          :disabled="readOnly || isWarrantyLinked"
                          placeholder="IMEI / Serial"
                        ></b-form-input>
                        <small v-if="!form.imei && !readOnly" class="text-muted">
                          Bắt buộc trước khi chuyển trạng thái — sau "Tiếp nhận" sẽ bị khoá.
                        </small>
                      </b-form-group>
                    </b-col>
                  </b-row>
                  <b-row>
                    <b-col md="12">
                      <b-form-group>
                        <label>Mô tả tình trạng (KH cung cấp)</label>
                        <b-form-textarea
                          v-model.trim="form.customerDescription"
                          rows="2"
                          size="sm"
                          :disabled="readOnly"
                        ></b-form-textarea>
                      </b-form-group>
                    </b-col>
                  </b-row>
                </b-card-body>
              </b-card>

              <!-- Section: Bảo hành -->
              <b-card no-body class="mb-2">
                <b-card-header class="py-2 bg-light">
                  <strong class="text-primary">Bảo hành</strong>
                </b-card-header>
                <b-card-body>
                  <b-row>
                    <b-col md="4">
                      <b-form-group>
                        <label>
                          Gói bảo hành (Sản phẩm bảo hành)
                          <span v-if="!form.isExternalDevice" class="text-danger">*</span>
                          <small v-if="form.warrantyProductId" class="text-success ml-1">
                            <i class="fas fa-check"></i> đã chọn
                          </small>
                        </label>
                        <AutoSuggest
                          :model="searchWarrantyProductText"
                          :suggestions="warrantyProductOptions"
                          placeholder="tên / mã SP bảo hành (productType=5)"
                          :limit="20"
                          :disabled="readOnly"
                          suggestionName="productName"
                          @change="onInputWarrantyProduct"
                          @select="onSelectWarrantyProduct"
                        >
                          <template #custom="{ suggestion }">
                            <div>
                              <div>{{ suggestion.item.productName }}</div>
                              <small class="text-muted">
                                {{ suggestion.item.productCode }}
                                · BH {{ suggestion.item.warrantyMonthNo || 0 }} tháng
                              </small>
                            </div>
                          </template>
                        </AutoSuggest>
                        <small v-if="!form.isExternalDevice && !form.warrantyProductId" class="text-danger">
                          Hàng DDV bắt buộc chọn Sản phẩm bảo hành.
                        </small>
                      </b-form-group>
                    </b-col>
                    <b-col md="3">
                      <b-form-group>
                        <label>Bắt đầu BH</label>
                        <b-input-group>
                          <date-picker
                            placeholder="dd/mm/yyyy"
                            class="form-control form-control-sm"
                            :config="dpConfigs.date"
                            v-model="form.warrantyStartDate"
                            :disabled="readOnly || isWarrantyLinked"
                          ></date-picker>
                        </b-input-group>
                      </b-form-group>
                    </b-col>
                    <b-col md="2">
                      <b-form-group>
                        <label>Số tháng BH</label>
                        <b-form-input
                          type="number"
                          :value="form.warrantyPeriodMonths"
                          size="sm"
                          disabled
                        ></b-form-input>
                        <small v-if="deviceWarrantyMonths || warrantyProductMonths" class="text-muted">
                          = máy {{ deviceWarrantyMonths }}T + gói {{ warrantyProductMonths }}T
                        </small>
                      </b-form-group>
                    </b-col>
                    <b-col md="3">
                      <b-form-group>
                        <label>
                          <span
                            v-b-tooltip.hover.top
                            title="Thu công sửa (labor). Linh kiện có giá vẫn lên Bill kể cả khi tắt. Khi máy còn BH → tự động lock = Miễn phí."
                            style="cursor: help; border-bottom: 1px dotted #888;"
                          >
                            Thu công sửa?
                          </span>
                          <span class="text-muted small ml-1">(?)</span>
                        </label>
                        <b-form-radio-group
                          v-model="form.isChargeCustomer"
                          size="sm"
                          :disabled="readOnly || isInWarranty"
                        >
                          <b-form-radio :value="true">Có thu phí</b-form-radio>
                          <b-form-radio :value="false">Miễn phí</b-form-radio>
                        </b-form-radio-group>
                        <small v-if="isInWarranty" class="text-success d-block mt-1">
                          <i class="fas fa-shield-alt"></i>
                          Máy còn BH — tự động miễn công.
                        </small>
                        <b-badge
                          v-if="warrantyStatusBadge"
                          :variant="warrantyStatusBadge.variant"
                          class="mt-1"
                        >
                          {{ warrantyStatusBadge.text }}
                        </b-badge>
                      </b-form-group>
                    </b-col>
                  </b-row>
                </b-card-body>
              </b-card>
            </div>
            <!-- /STEP 1 -->

            <!-- ============== STEP 2: Mã lỗi & Dịch vụ ============== -->
            <div class="pb-5" data-wizard-type="step-content">
              <!-- Section: Mã lỗi -->
              <b-card no-body class="mb-4">
                <b-card-header class="py-2 bg-light d-flex justify-content-between align-items-center">
                  <strong class="text-primary">Mã lỗi chẩn đoán</strong>
                  <small class="text-muted">Có thể chọn nhiều mã lỗi</small>
                </b-card-header>
                <b-card-body>
                  <b-row class="mb-3" v-if="!readOnly">
                    <b-col md="12">
                      <AutoSuggest
                        :model="errorCodeSearchText"
                        :suggestions="filteredErrorCodeOptions"
                        suggestionName="displayName"
                        placeholder="mã lỗi (gõ mã hoặc tên để tìm)"
                        :limit="50"
                        @change="onSearchErrorCode"
                        @select="onSelectErrorCode"
                      />
                    </b-col>
                  </b-row>
                  <b-table
                    v-if="form.errorCodes.length > 0"
                    :items="form.errorCodes"
                    :fields="errorCodeFields"
                    bordered
                    small
                    class="table-vertical-center"
                  >
                    <template #cell(stt)="data">{{ data.index + 1 }}</template>
                    <template #cell(actions)="data">
                      <b-button
                        v-if="!readOnly"
                        variant="outline-danger"
                        size="sm"
                        @click="removeErrorCode(data.index)"
                      >
                        <i class="fas fa-times p-0"></i>
                      </b-button>
                    </template>
                  </b-table>
                  <p v-else class="text-muted small mb-0">Chưa có mã lỗi nào.</p>
                </b-card-body>
              </b-card>

              <!-- Section: Hạng mục dịch vụ -->
              <b-card no-body class="mb-2">
                <b-card-header class="py-2 bg-light d-flex justify-content-between align-items-center">
                  <strong class="text-primary">Hạng mục dịch vụ</strong>
                  <small class="text-muted">Checklist công việc</small>
                </b-card-header>
                <b-card-body>
                  <b-row class="mb-3" v-if="!readOnly">
                    <b-col md="12">
                      <AutoSuggest
                        :model="serviceSearchText"
                        :suggestions="filteredServiceOptions"
                        suggestionName="displayName"
                        placeholder="hạng mục dịch vụ (gõ mã hoặc tên để tìm)"
                        :limit="50"
                        @change="onSearchService"
                        @select="onSelectService"
                      />
                    </b-col>
                  </b-row>
                  <b-table
                    v-if="form.services.length > 0"
                    :items="form.services"
                    :fields="serviceFields"
                    bordered
                    small
                    class="table-vertical-center"
                  >
                    <template #cell(stt)="data">{{ data.index + 1 }}</template>
                    <template #cell(note)="data">
                      <b-form-input
                        v-model.trim="data.item.note"
                        size="sm"
                        :disabled="readOnly"
                      ></b-form-input>
                    </template>
                    <template #cell(actions)="data">
                      <b-button
                        v-if="!readOnly"
                        variant="outline-danger"
                        size="sm"
                        @click="removeService(data.index)"
                      >
                        <i class="fas fa-times p-0"></i>
                      </b-button>
                    </template>
                  </b-table>
                  <p v-else class="text-muted small mb-0">Chưa có hạng mục dịch vụ nào.</p>
                </b-card-body>
              </b-card>

              <!-- M6: Sản phẩm xuất hoá đơn (Linh kiện + SP dịch vụ) -->
              <b-card no-body class="mb-2">
                <b-card-header class="py-2 bg-light d-flex justify-content-between align-items-center">
                  <strong class="text-primary">Sản phẩm xuất hoá đơn</strong>
                  <small class="text-muted">
                    Linh kiện (1) + Combo (3) + SP dịch vụ (4)
                  </small>
                </b-card-header>
                <b-card-body>
                  <b-alert v-if="!isEdit" variant="info" show class="mb-2 py-2">
                    <i class="fas fa-info-circle"></i>
                    Linh kiện chỉ thêm được sau khi tạo phiếu (lưu phiếu trước, mở lại để thêm linh kiện).
                  </b-alert>
                  <template v-else>
                    <b-row class="mb-2" v-if="!partReadOnly">
                      <b-col md="12">
                        <AutoSuggest
                          :model="searchPartText"
                          :suggestions="partSearchOptions"
                          :placeholder="addingPart
                            ? 'Đang thêm...'
                            : 'Chọn sản phẩm để thêm vào danh sách'"
                          :limit="20"
                          :disabled="partReadOnly || addingPart"
                          suggestionName="productName"
                          @change="onInputPart"
                          @select="onSelectPart"
                        >
                          <template #custom="{ suggestion }">
                            <div class="d-flex justify-content-between align-items-center">
                              <div>
                                <div>{{ suggestion.item.productName }}</div>
                                <small class="text-muted">
                                  {{ suggestion.item.productCode }}
                                  <b-badge :variant="partTypeVariant(suggestion.item.productType)" class="ml-1">
                                    {{ partTypeLabel(suggestion.item.productType) }}
                                  </b-badge>
                                  <span
                                    v-if="Number(suggestion.item.productType) === 1"
                                    :class="suggestion.item.quantityInStock > 0 ? 'text-success' : 'text-danger'"
                                    class="ml-1"
                                  >
                                    (Tồn CTB: {{ suggestion.item.quantityInStock || 0 }})
                                  </span>
                                </small>
                              </div>
                              <small class="text-success">
                                {{ formatPrice(suggestion.item.sellingPrice) }} đ
                              </small>
                            </div>
                          </template>
                        </AutoSuggest>
                      </b-col>
                    </b-row>
                    <b-alert v-else variant="warning" show class="py-2">
                      <i class="fas fa-lock"></i>
                      Phiếu ở trạng thái "{{ statusLabel(form.status) }}" — không thể thêm/sửa linh kiện.
                    </b-alert>

                    <b-table
                      v-if="parts.length > 0"
                      :items="parts"
                      :fields="partFields"
                      bordered
                      small
                      class="mb-0 table-vertical-center"
                    >
                      <template #cell(stt)="data">{{ data.index + 1 }}</template>
                      <template #cell(productType)="{ item }">
                        <b-badge :variant="partTypeVariant(item.productType)">
                          {{ partTypeLabel(item.productType) }}
                        </b-badge>
                      </template>
                      <template #cell(quantity)="{ item }">
                        <b-form-input
                          type="number"
                          v-model.number="item.quantity"
                          min="1"
                          size="sm"
                          :disabled="partReadOnly"
                          @change="onPartChanged(item)"
                        ></b-form-input>
                      </template>
                      <template #cell(price)="{ item }">
                        <b-form-input
                          type="number"
                          v-model.number="item.price"
                          min="0"
                          size="sm"
                          :disabled="partReadOnly"
                          @change="onPartChanged(item)"
                        ></b-form-input>
                      </template>
                      <template #cell(warrantyMonth)="{ item }">
                        <b-form-input
                          type="number"
                          v-model.number="item.warrantyMonth"
                          min="0"
                          size="sm"
                          :disabled="partReadOnly"
                          @change="onPartChanged(item)"
                        ></b-form-input>
                      </template>
                      <template #cell(lineTotal)="{ item }">
                        <strong>{{ formatPrice(computeLineTotal(item)) }}</strong>
                      </template>
                      <template #cell(actions)="{ item }">
                        <b-button
                          v-if="!partReadOnly"
                          variant="outline-danger"
                          size="sm"
                          @click="removePart(item)"
                        >
                          <i class="fas fa-times p-0"></i>
                        </b-button>
                      </template>
                    </b-table>
                    <p v-else class="text-muted small mb-0">Chưa có linh kiện / SP dịch vụ nào.</p>

                    <div v-if="parts.length > 0" class="mt-3 p-3 bg-light rounded">
                      <div class="d-flex justify-content-between">
                        <span>Cộng SP dịch vụ:</span>
                        <strong>{{ formatPrice(partsTotalServices) }} đ</strong>
                      </div>
                      <div class="d-flex justify-content-between">
                        <span>Cộng linh kiện:</span>
                        <strong>{{ formatPrice(partsTotalParts) }} đ</strong>
                      </div>
                      <div v-if="partsTotalCombos > 0" class="d-flex justify-content-between">
                        <span>Cộng combo:</span>
                        <strong>{{ formatPrice(partsTotalCombos) }} đ</strong>
                      </div>
                      <div class="d-flex justify-content-between border-top mt-2 pt-2">
                        <strong>Tổng tiền dự kiến:</strong>
                        <strong class="text-primary">{{ formatPrice(partsTotalAll) }} đ</strong>
                      </div>
                      <div
                        class="d-flex justify-content-between pt-1"
                        v-b-tooltip.hover.top
                        title="Số tiền sẽ snapshot vào Hoá đơn bán lẻ auto-tạo khi chuyển sang 'Đã trả máy'."
                      >
                        <span class="text-muted">
                          <i class="fas fa-file-invoice mr-1"></i>
                          Tiền lên hoá đơn bán lẻ:
                        </span>
                        <strong class="text-success">{{ formatPrice(partsTotalBillable) }} đ</strong>
                      </div>
                    </div>
                  </template>
                </b-card-body>
              </b-card>
            </div>
            <!-- /STEP 2 -->

            <!-- ============== STEP 3: Nhân sự & Ghi chú ============== -->
            <div class="pb-5" data-wizard-type="step-content">
              <b-card no-body class="mb-4">
                <b-card-header class="py-2 bg-light d-flex justify-content-between align-items-center">
                  <strong class="text-primary">Phân công nhân sự</strong>
                  <small v-if="techniciansSaving" class="text-muted">
                    <b-spinner small></b-spinner>
                    Đang lưu nhân sự...
                  </small>
                  <small v-else-if="isEdit && !readOnly" class="text-success">
                    <i class="fas fa-check-circle"></i> Tự động lưu khi thêm/xoá
                  </small>
                </b-card-header>
                <b-card-body>
                  <b-alert v-if="!form.storeId && !readOnly" variant="warning" show class="mb-3 py-2">
                    <i class="fas fa-info-circle"></i>
                    Chưa chọn cửa hàng. Vui lòng quay về Tab 1 chọn cửa hàng trước —
                    sau đó danh sách nhân viên của cửa hàng sẽ tự load vào 3 ô bên dưới.
                  </b-alert>
                  <p v-else-if="!readOnly && form.storeId" class="text-muted small mb-3">
                    Đã load <strong>{{ employeeOptions.length }}</strong> nhân viên thuộc cửa hàng đang chọn.
                    Gõ tên để lọc — chọn từ dropdown để <strong>thêm</strong>. Mỗi vai trò chọn được nhiều người.
                  </p>
                  <b-row>
                    <b-col md="4">
                      <b-form-group>
                        <label>Kỹ thuật viên chính</label>
                        <AutoSuggest
                          v-if="!readOnly"
                          :model="technicianName"
                          :suggestions="filteredEmployeeOptions"
                          :placeholder="'kỹ thuật viên'"
                          :limit="20"
                          suggestionName="displayName"
                          @change="(t) => onInputEmployee(t, 'technician')"
                          @select="(opt) => onSelectEmployee(opt, 'technician')"
                        />
                        <div class="mt-1">
                          <b-badge
                            v-for="uid in form.technicianIds"
                            :key="uid"
                            variant="primary"
                            class="mr-1 mb-1 tech-chip"
                          >
                            {{ employeeName(uid) }}
                            <a
                              v-if="!readOnly"
                              href="#"
                              class="tech-chip-x"
                              @click.prevent="removeTechnician('technicianIds', uid)"
                            >×</a>
                          </b-badge>
                          <span
                            v-if="readOnly && form.technicianIds.length === 0"
                            class="text-muted font-italic small"
                          >— chưa gán —</span>
                        </div>
                        <small class="text-muted">Người trực tiếp sửa</small>
                      </b-form-group>
                    </b-col>
                    <b-col md="4">
                      <b-form-group>
                        <label>Kỹ thuật viên kiểm tra</label>
                        <AutoSuggest
                          v-if="!readOnly"
                          :model="checkTechnicianName"
                          :suggestions="filteredEmployeeOptions"
                          :placeholder="'KTV kiểm tra'"
                          :limit="20"
                          suggestionName="displayName"
                          @change="(t) => onInputEmployee(t, 'checkTechnician')"
                          @select="(opt) => onSelectEmployee(opt, 'checkTechnician')"
                        />
                        <div class="mt-1">
                          <b-badge
                            v-for="uid in form.checkTechnicianIds"
                            :key="uid"
                            variant="info"
                            class="mr-1 mb-1 tech-chip"
                          >
                            {{ employeeName(uid) }}
                            <a
                              v-if="!readOnly"
                              href="#"
                              class="tech-chip-x"
                              @click.prevent="removeTechnician('checkTechnicianIds', uid)"
                            >×</a>
                          </b-badge>
                          <span
                            v-if="readOnly && form.checkTechnicianIds.length === 0"
                            class="text-muted font-italic small"
                          >— chưa gán —</span>
                        </div>
                        <small class="text-muted">QC sau khi sửa</small>
                      </b-form-group>
                    </b-col>
                    <b-col md="4">
                      <b-form-group>
                        <label>Điều phối</label>
                        <AutoSuggest
                          v-if="!readOnly"
                          :model="coordinatorName"
                          :suggestions="filteredEmployeeOptions"
                          :placeholder="'điều phối'"
                          :limit="20"
                          suggestionName="displayName"
                          @change="(t) => onInputEmployee(t, 'coordinator')"
                          @select="(opt) => onSelectEmployee(opt, 'coordinator')"
                        />
                        <div class="mt-1">
                          <b-badge
                            v-for="uid in form.coordinatorIds"
                            :key="uid"
                            variant="secondary"
                            class="mr-1 mb-1 tech-chip"
                          >
                            {{ employeeName(uid) }}
                            <a
                              v-if="!readOnly"
                              href="#"
                              class="tech-chip-x"
                              @click.prevent="removeTechnician('coordinatorIds', uid)"
                            >×</a>
                          </b-badge>
                          <span
                            v-if="readOnly && form.coordinatorIds.length === 0"
                            class="text-muted font-italic small"
                          >— chưa gán —</span>
                        </div>
                        <small class="text-muted">Người phân công</small>
                      </b-form-group>
                    </b-col>
                  </b-row>
                </b-card-body>
              </b-card>

              <b-card no-body class="mb-2">
                <b-card-header class="py-2 bg-light">
                  <strong class="text-primary">Lịch hẹn & ghi chú</strong>
                </b-card-header>
                <b-card-body>
                  <b-row>
                    <b-col md="4">
                      <b-form-group>
                        <label>Hẹn trả máy</label>
                        <b-input-group>
                          <date-picker
                            placeholder="dd/mm/yyyy"
                            class="form-control form-control-sm"
                            :config="dpConfigs.date"
                            v-model="form.appointmentDate"
                            :disabled="readOnly"
                          ></date-picker>
                        </b-input-group>
                      </b-form-group>
                    </b-col>
                  </b-row>
                  <b-form-group>
                    <label>Ghi chú KTV</label>
                    <b-form-textarea
                      v-model.trim="form.technicianNote"
                      rows="3"
                      size="sm"
                      :disabled="readOnly"
                      placeholder="VD: Máy còn nguyên tem, không bị nứt vỡ. Màn sọc 1 vạch dọc bên phải, pin SOH 78%."
                    ></b-form-textarea>
                  </b-form-group>

                  <b-alert v-if="readOnly" variant="warning" show class="mt-2 mb-0">
                    <i class="fas fa-exclamation-triangle"></i>
                    Phiếu đã chuyển trạng thái khỏi "Tiếp nhận" — chỉ có thể xem, không sửa.
                  </b-alert>
                </b-card-body>
              </b-card>
            </div>
            <!-- /STEP 3 -->

            <!-- ============== STEP 4: Tài liệu (evidence ảnh) ============== -->
            <div
              v-if="isEdit"
              class="pb-5"
              data-wizard-type="step-content"
            >
              <b-card no-body class="mb-2">
                <b-card-header class="py-2 bg-light">
                  <strong class="text-primary">
                    <i class="fas fa-images mr-1"></i>
                    Ảnh hiện trạng máy / Tài liệu đính kèm
                  </strong>
                </b-card-header>
                <b-card-body>
                  <b-alert show variant="info" class="mb-3 py-2">
                    <i class="fas fa-info-circle mr-1"></i>
                    Đính kèm ảnh hiện trạng máy / hồ sơ để lưu vết (không bắt buộc).
                  </b-alert>
                  <!--
                    Tài liệu: tách khỏi readOnly form. Phiếu đã returned/cancelled vẫn cần upload
                    bổ sung (ảnh giao máy, biên bản, hồ sơ thanh toán...). Chỉ disable khi user
                    thiếu quyền UPDATE.
                  -->
                  <FileUploadManager
                    :entity-id="form.id"
                    entity="repair-slips"
                    :disabled="!canManageFiles"
                  />
                </b-card-body>
              </b-card>
            </div>
            <!-- /STEP 4 -->

            <!-- ============== STEP 5: Lịch sử ============== -->
            <div
              v-if="isEdit"
              class="pb-5"
              data-wizard-type="step-content"
            >
              <div class="d-flex justify-content-end mb-3">
                <b-button
                  variant="outline-secondary"
                  size="sm"
                  :disabled="loadingHistories"
                  @click="loadHistories"
                >
                  <i class="fas fa-sync mr-1"></i>Làm mới
                </b-button>
              </div>
              <b-table
                :items="histories"
                :fields="historyFields"
                :busy="loadingHistories"
                show-empty
                empty-text="Chưa có lịch sử nào"
                bordered
                small
                class="mb-0 table-vertical-center"
              >
                <template #table-busy>
                  <div class="text-center text-primary my-2">
                    <b-spinner class="align-middle"></b-spinner>
                    <strong>Đang tải...</strong>
                  </div>
                </template>
                <template #cell(createdAt)="{ item }">
                  {{ formatDateTime(item.createdAt) }}
                </template>
                <template #cell(actionType)="{ item }">
                  <b-badge :variant="historyActionVariant(item.actionType)">
                    {{ historyActionLabel(item.actionType) }}
                  </b-badge>
                </template>
                <template #cell(change)="{ item }">
                  <span v-if="item.actionType === 'status_change'">
                    <b-badge :variant="statusVariant(item.oldValue)">
                      {{ statusLabel(item.oldValue) }}
                    </b-badge>
                    <i class="fas fa-arrow-right mx-1 text-muted"></i>
                    <b-badge :variant="statusVariant(item.newValue)">
                      {{ statusLabel(item.newValue) }}
                    </b-badge>
                  </span>
                  <span v-else-if="item.actionType === 'refund'">
                    <strong>{{ formatPrice(parseRefundAmount(item.newValue)) }} đ</strong>
                    <span v-if="parseRefundReason(item.newValue)" class="text-muted ml-1">
                      — {{ parseRefundReason(item.newValue) }}
                    </span>
                  </span>
                  <!-- Phase 1: CREATE với snapshot data ban đầu -->
                  <div
                    v-else-if="
                      item.actionType === 'create' &&
                      parseHistoryValue(item.newValue) &&
                      parseHistoryValue(item.newValue).snapshot
                    "
                    class="history-snapshot"
                  >
                    <div class="text-muted small mb-1">Khởi tạo phiếu với:</div>
                    <div
                      v-for="(val, field) in parseHistoryValue(item.newValue).snapshot"
                      :key="field"
                      class="small history-diff-line"
                    >
                      <span class="history-field-label">{{ historyFieldLabel(field) }}:</span>
                      <span :title="String(val)">{{ formatHistoryFieldValue(val) }}</span>
                    </div>
                  </div>
                  <!-- Phase 1: UPDATE với diff array changes[] -->
                  <div
                    v-else-if="
                      item.actionType === 'update' &&
                      parseHistoryValue(item.newValue) &&
                      Array.isArray(parseHistoryValue(item.newValue).changes)
                    "
                    class="history-diff"
                  >
                    <div
                      v-for="(c, idx) in parseHistoryValue(item.newValue).changes"
                      :key="idx"
                      class="small history-diff-line"
                    >
                      <span class="history-field-label">{{ historyFieldLabel(c.field) }}:</span>
                      <span class="text-muted" :title="String(c.oldValue)">
                        {{ formatHistoryFieldValue(c.oldValue) }}
                      </span>
                      <span class="history-arrow">→</span>
                      <span :title="String(c.newValue)">
                        {{ formatHistoryFieldValue(c.newValue) }}
                      </span>
                    </div>
                  </div>
                  <!-- Fallback cho record legacy (CREATE/UPDATE format cũ) hoặc link-bill -->
                  <span v-else-if="item.fieldName">
                    <code>{{ item.fieldName }}</code>
                  </span>
                  <span v-else class="text-muted">—</span>
                </template>
                <template #cell(note)="{ item }">
                  <span v-if="item.note">{{ item.note }}</span>
                  <span v-else class="text-muted">—</span>
                </template>
                <template #cell(changedBy)="{ item }">
                  <div v-if="item.changedByName">
                    <div>{{ item.changedByName }}</div>
                    <small v-if="item.changedByCode" class="text-muted">
                      {{ item.changedByCode }}
                    </small>
                  </div>
                  <span v-else class="text-muted">#{{ item.changedBy }}</span>
                </template>
              </b-table>
            </div>
            <!-- /STEP 5 -->

                  </div><!-- /col -->
                </div><!-- /row -->
              </div><!-- /card-body -->
            </div><!-- /card-shadowless -->
          </div>
          <!-- /Wizard -->
        </template>
      </KTCodePreview>
    </div>

    <!-- Modal: Xác nhận chuyển trạng thái -->
    <b-modal
      id="modal-transition"
      :title="`Chuyển sang: ${statusLabel(transition.toStatus)}`"
      hide-footer
      no-close-on-backdrop
      lazy
      @hidden="resetTransition"
      size="md"
    >
      <p>
        Phiếu <strong>{{ form.slipNumber || '' }}</strong> sẽ chuyển từ
        <b-badge :variant="statusVariant(form.status)">{{ statusLabel(form.status) }}</b-badge>
        sang
        <b-badge :variant="statusVariant(transition.toStatus)">{{ statusLabel(transition.toStatus) }}</b-badge>.
      </p>

      <!-- Hiển thị lỗi inline ngay trong modal — toast top-right dễ bị modal/backdrop che. -->
      <b-alert
        :show="!!transitionError"
        variant="danger"
        class="py-2 mb-3"
        dismissible
        @dismissed="transitionError = ''"
      >
        <i class="fas fa-exclamation-triangle mr-1"></i>
        {{ transitionError }}
      </b-alert>

      <b-form-group v-if="transitionRequiresVendor">
        <label>Đối tác / Hãng <span class="text-danger">*</span></label>
        <b-form-input
          v-model.trim="transition.vendorPartner"
          size="sm"
          placeholder="VD: Samsung Service Center..."
        ></b-form-input>
      </b-form-group>

      <b-form-group v-if="transitionRequiresCancelReason">
        <label>Lý do huỷ <span class="text-danger">*</span></label>
        <b-form-select
          v-model="transition.cancelReason"
          :options="cancelReasonOptions"
          size="sm"
        ></b-form-select>
      </b-form-group>

      <b-form-group v-if="isTerminalChargeStatus">
        <label>Phí chẩn đoán (BER / từ chối báo giá)</label>
        <b-input-group append="đ" size="sm">
          <b-form-input
            type="number"
            v-model.number="transition.diagnosisCharge"
            min="0"
          ></b-form-input>
        </b-input-group>
      </b-form-group>

      <!-- REQ-F5.5: ràng buộc bắt buộc khi → Hoàn thành -->
      <template v-if="isCompletedTransition">
        <b-form-group>
          <label>Loại trả <span class="text-danger">*</span></label>
          <b-form-select
            v-model="transition.returnTypeId"
            :options="returnTypeOptions"
            size="sm"
          ></b-form-select>
          <small class="text-muted">
            return_ok = thu phí/bảo hành · return_out = không sửa được · return_free = miễn phí.
          </small>
        </b-form-group>

        <b-alert
          :show="showReturnOutWithPartsWarning"
          variant="warning"
          class="py-2 mb-3"
        >
          <i class="fas fa-exclamation-triangle mr-1"></i>
          Phiếu đã chọn <strong>"Không sửa được, trả máy"</strong> nhưng vẫn còn
          <strong>{{ parts.length }}</strong> sản phẩm xuất hoá đơn.
          Vui lòng đóng modal, xoá hết Linh kiện / SP dịch vụ ở mục
          <em>"Sản phẩm xuất hoá đơn"</em> trước khi chuyển sang
          <strong>Hoàn thành</strong> — nếu giữ lại, Bill bán lẻ sẽ vẫn được tạo.
        </b-alert>

        <b-form-group>
          <label>KTV kiểm tra <span class="text-danger">*</span></label>
          <AutoSuggest
            :model="checkTechnicianModalSearch"
            :suggestions="filteredEmployeeOptions"
            placeholder="Tên KTV QC"
            :limit="20"
            suggestionName="displayName"
            @change="(t) => onInputEmployee(t, 'modalCheckTechnician')"
            @select="(opt) => onSelectEmployee(opt, 'modalCheckTechnician')"
          />
          <div class="mt-1">
            <b-badge
              v-for="uid in transition.checkTechnicianIds"
              :key="uid"
              variant="info"
              class="mr-1 mb-1 tech-chip"
            >
              {{ employeeName(uid) }}
              <a
                href="#"
                class="tech-chip-x"
                @click.prevent="removeTechnician('modalCheckTechnicianIds', uid)"
              >×</a>
            </b-badge>
          </div>
        </b-form-group>

        <b-form-group>
          <label>Ghi chú KTV <span class="text-danger">*</span></label>
          <b-form-textarea
            v-model.trim="transition.technicianNote"
            rows="3"
            size="sm"
            placeholder="VD: Thay pin hãng, vệ sinh main, test 24h ổn định."
          ></b-form-textarea>
          <small class="text-muted">
            Mô tả công việc đã sửa — cần thiết cho lần bảo hành sau (REQ-F5.5).
          </small>
        </b-form-group>
      </template>

      <b-form-group>
        <label>
          Ghi chú
          <span v-if="transitionRequiresNote" class="text-danger">*</span>
        </label>
        <b-form-textarea
          v-model.trim="transition.note"
          rows="3"
          size="sm"
          placeholder="Ghi rõ lý do/chi tiết chuyển trạng thái..."
        ></b-form-textarea>
        <small v-if="transitionRequiresNote" class="text-muted">
          Trạng thái này bắt buộc nhập ghi chú (REQ-F5.3).
        </small>
      </b-form-group>

      <div class="d-flex justify-content-end mt-3">
        <b-button
          variant="secondary"
          size="sm"
          class="mr-2"
          @click="$bvModal.hide('modal-transition')"
        >
          Huỷ
        </b-button>
        <b-button
          variant="primary"
          size="sm"
          :disabled="transitionSubmitting"
          @click="confirmTransition"
        >
          <i class="fas fa-check"></i>
          {{ transitionSubmitting ? 'Đang xử lý...' : 'Xác nhận' }}
        </b-button>
      </div>
    </b-modal>

    <!-- Modal: Hoàn tiền (M8 + REQ-F6.3) -->
    <b-modal
      id="modal-refund"
      title="Hoàn tiền cho khách hàng"
      hide-footer
      no-close-on-backdrop
      lazy
      @hidden="resetRefund"
      size="md"
    >
      <b-alert variant="warning" show class="py-2">
        <i class="fas fa-exclamation-triangle"></i>
        Chỉ ghi nhận <strong>yêu cầu hoàn tiền</strong> trên phiếu sửa chữa
        (số tiền + lý do). <strong>KHÔNG tạo hoá đơn</strong>, không thanh toán.
        <br />
        Hoàn tiền thật cho khách phải làm qua phiếu thanh toán hoàn tiền thủ công.
        Mỗi phiếu chỉ ghi nhận được <strong>1 lần</strong>.
      </b-alert>

      <!-- Inline error — toast top-right dễ bị modal/backdrop che. -->
      <b-alert
        :show="!!refundError"
        variant="danger"
        class="py-2 mb-3"
        dismissible
        @dismissed="refundError = ''"
      >
        <i class="fas fa-exclamation-triangle mr-1"></i>
        {{ refundError }}
      </b-alert>

      <b-form-group>
        <label>Số tiền hoàn (VND) <span class="text-danger">*</span></label>
        <b-input-group append="đ" size="sm">
          <b-form-input
            type="number"
            v-model.number="refund.amount"
            min="0"
            placeholder="VD: 500000"
          ></b-form-input>
        </b-input-group>
      </b-form-group>

      <b-form-group>
        <label>Lý do hoàn tiền <span class="text-danger">*</span></label>
        <b-form-select v-model="refund.reason" size="sm" :options="refundReasonOptions"></b-form-select>
      </b-form-group>

      <b-form-group>
        <label>Ghi chú</label>
        <b-form-textarea
          v-model.trim="refund.note"
          rows="2"
          size="sm"
          placeholder="Chi tiết bổ sung (tuỳ chọn)..."
        ></b-form-textarea>
      </b-form-group>

      <div class="d-flex justify-content-end mt-3">
        <b-button
          variant="secondary"
          size="sm"
          class="mr-2"
          @click="$bvModal.hide('modal-refund')"
        >
          Huỷ
        </b-button>
        <b-button
          variant="danger"
          size="sm"
          :disabled="refundSubmitting || !refund.amount || !refund.reason"
          @click="confirmRefund"
        >
          <i class="fas fa-money-bill-wave"></i>
          {{ refundSubmitting ? 'Đang xử lý...' : 'Xác nhận hoàn tiền' }}
        </b-button>
      </div>
    </b-modal>

    <!-- Modal: Hoàn tác trả máy (Decision #9) -->
    <b-modal
      id="modal-undo-return"
      title="Hoàn tác trạng thái — trả máy"
      hide-footer
      no-close-on-backdrop
      lazy
      @hidden="undoReturnNote = ''"
      size="md"
    >
      <b-alert variant="warning" show class="py-2">
        <i class="fas fa-exclamation-triangle"></i>
        Phiếu sẽ chuyển từ
        <b-badge variant="success">Đã trả máy</b-badge>
        về
        <b-badge variant="success">Đã thông báo KH</b-badge>
        — chỉ áp dụng nếu phát hiện sai sót khi trả máy.
      </b-alert>
      <p class="text-muted small">
        Ràng buộc:
        <ul class="small mb-0">
          <li>Trong 24 giờ kể từ thời điểm trả máy</li>
          <li>Phiếu CHƯA xuất hoá đơn dịch vụ (nếu có Bill phải dùng Refund flow)</li>
          <li>Bắt buộc nhập lý do (≥5 ký tự, audit log)</li>
        </ul>
      </p>

      <b-form-group>
        <label>
          Lý do hoàn tác <span class="text-danger">*</span>
        </label>
        <b-form-textarea
          v-model.trim="undoReturnNote"
          rows="3"
          size="sm"
          placeholder="VD: Khách quên đồ trong máy, cần kiểm tra lại trước khi giao..."
        ></b-form-textarea>
      </b-form-group>

      <div class="d-flex justify-content-end mt-3">
        <b-button
          variant="secondary"
          size="sm"
          class="mr-2"
          @click="$bvModal.hide('modal-undo-return')"
        >
          Huỷ
        </b-button>
        <b-button
          variant="warning"
          size="sm"
          :disabled="undoReturnSubmitting || (undoReturnNote || '').trim().length < 5"
          @click="confirmUndoReturn"
        >
          <i class="fas fa-undo"></i>
          {{ undoReturnSubmitting ? 'Đang xử lý...' : 'Xác nhận hoàn tác' }}
        </b-button>
      </div>
    </b-modal>

  </div>
</template>

<script>
import KTCodePreview from '@/view/content/CodePreview.vue';
import ApiService from '@/core/services/api.service';
import { validationMixin } from 'vuelidate';
import { required, maxLength } from 'vuelidate/lib/validators';
import { checkPermission } from '@/utils/saveDataToLocal';
import {
  makeToastSuccess,
  makeToastFaile,
  removeAccents,
} from '@/utils/common';
import { DP_CONFIG } from '@/utils/date';
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module';
import AutoSuggest from '@/view/base/auto-sugguest/AutoSuggest';
import FileUploadManager from '@/view/modules/upload/FileUploadManager.vue';
// Treeselect cho dropdown Tỉnh/TP + Phường/Xã (gõ tìm — pattern giống CustomerInfo).
import Treeselect from '@riophae/vue-treeselect';
import '@riophae/vue-treeselect/dist/vue-treeselect.css';
import {
  SLIP_STATUS,
  SLIP_STATUS_OPTIONS as STATUS_OPTIONS,
  SLIP_STATE_TRANSITIONS as STATE_TRANSITIONS,
  SLIP_STATUSES_REQUIRE_NOTE as REQUIRES_NOTE,
  SLIP_STATUSES_REQUIRE_VENDOR as REQUIRES_VENDOR,
  SLIP_STATUSES_REQUIRE_CANCEL_REASON as REQUIRES_CANCEL_REASON,
  SLIP_STATUSES_PART_EDITABLE as PART_EDITABLE_STATUSES,
  SLIP_TERMINAL_CHARGE_STATUSES as TERMINAL_CHARGE_STATUSES,
  CANCEL_REASON_OPTIONS,
  ORDER_SOURCE_OPTIONS,
  REFUND_REASON_OPTIONS,
  HISTORY_ACTION,
  HISTORY_ACTION_META as HISTORY_ACTION_LABELS,
  PRODUCT_TYPE,
  RETURN_TYPE_CODE,
  UNDO_RETURN_WINDOW_MS,
  UNDO_RETURN_MIN_NOTE_LEN,
} from '@/utils/warranty-service.constants';
import {
  extractError,
  formatPrice,
  formatDateTime,
  toIsoDate,
  fromIsoDate,
} from '@/utils/warranty-service.helpers';
import KTWizard from '@/assets/js/components/wizard';
import debounce from 'debounce';
import moment from 'moment';

// Table fields — frozen ở module scope, không cần Vue reactive wrap.
const HISTORY_FIELDS = Object.freeze([
  { key: 'createdAt', label: 'Thời gian', thStyle: { width: '17%' } },
  { key: 'actionType', label: 'Thao tác', thStyle: { width: '15%' } },
  { key: 'change', label: 'Thay đổi', thStyle: { width: '30%' } },
  { key: 'note', label: 'Ghi chú' },
  { key: 'changedBy', label: 'Người thực hiện', thStyle: { width: '14%' } },
]);

// Map field-name BE → label tiếng Việt (Phase 1 audit log).
// Mirror SNAPSHOT_FIELDS phía BE — khi BE thêm field whitelist mới, bổ sung ở đây để render đẹp.
const HISTORY_FIELD_LABELS_VI = Object.freeze({
  customerName: 'Tên KH',
  customerPhone: 'SĐT',
  customerEmail: 'Email',
  customerAddress: 'Địa chỉ',
  customerCityId: 'Tỉnh / TP',
  customerWardId: 'Phường / Xã',
  isExternalDevice: 'Hàng ngoài',
  productName: 'Sản phẩm',
  brandName: 'Hãng',
  color: 'Màu',
  imei: 'IMEI',
  isChargeCustomer: 'Thu công sửa',
  warrantyProductName: 'Gói BH',
  warrantyStartDate: 'BH từ',
  warrantyPeriodMonths: 'Số tháng BH',
  warrantyEndDate: 'BH đến',
  orderSource: 'Nguồn KH',
  appointmentDate: 'Hẹn trả máy',
  customerDescription: 'Mô tả tình trạng',
  technicianNote: 'Ghi chú KTV',
  warrantyBillId: 'HĐ bảo hành',
  machinePasswordType: 'Loại mật khẩu',
});

const PART_FIELDS = Object.freeze([
  { key: 'stt', label: 'STT', tdClass: 'text-center', thStyle: { width: '4%' } },
  { key: 'productType', label: 'Loại', tdClass: 'text-center', thStyle: { width: '8%' } },
  { key: 'productCode', label: 'Mã SP', thStyle: { width: '12%' } },
  { key: 'productName', label: 'Tên sản phẩm', thStyle: { width: '24%' } },
  { key: 'quantity', label: 'SL', thStyle: { width: '6%' } },
  { key: 'price', label: 'Giá', thStyle: { width: '13%' } },
  { key: 'warrantyMonth', label: 'BH (tháng)', tdClass: 'text-center', thStyle: { width: '10%' } },
  { key: 'lineTotal', label: 'Thành tiền', tdClass: 'text-right', thStyle: { width: '13%' } },
  { key: 'actions', label: '', tdClass: 'text-center', thStyle: { width: '5%' } },
]);

const ERROR_CODE_FIELDS = Object.freeze([
  { key: 'stt', label: 'STT', tdClass: 'text-center', thStyle: { width: '5%' } },
  { key: 'errorCode', label: 'Mã lỗi', thStyle: { width: '15%' } },
  { key: 'errorName', label: 'Tên mã lỗi' },
  { key: 'actions', label: 'Xoá', tdClass: 'text-center', thStyle: { width: '8%' } },
]);

const SERVICE_FIELDS = Object.freeze([
  { key: 'stt', label: 'STT', tdClass: 'text-center', thStyle: { width: '5%' } },
  { key: 'repairCode', label: 'Mã DV', thStyle: { width: '15%' } },
  { key: 'repairName', label: 'Tên hạng mục', thStyle: { width: '40%' } },
  { key: 'note', label: 'Ghi chú', thStyle: { width: '32%' } },
  { key: 'actions', label: 'Xoá', tdClass: 'text-center', thStyle: { width: '8%' } },
]);

const initialRefund = () => ({ amount: 0, reason: null, note: '' });

const initialTransition = () => ({
  toStatus: '',
  note: '',
  cancelReason: null,
  vendorPartner: '',
  diagnosisCharge: 0,
  returnTypeId: null,
  checkTechnicianIds: [],
  technicianNote: '',
});

const API_BASE = 'v2/warranty-service/repair-slips';
const REPAIR_INFOS_API = 'v2/warranty-service/repair-infos/all/list';
const ERROR_CODES_API = 'v2/warranty-service/repair-error-codes/all/list';
const STORES_API = 'stores/getStores';

const initialForm = () => ({
  storeId: null,
  customerId: null,
  customerName: '',
  customerPhone: '',
  customerAddress: '',
  // Tỉnh/TP + Phường/Xã (Vietnam 2-tier reform 2025-07) — null = chưa chọn.
  customerCityId: null,
  customerWardId: null,
  customerEmail: '',
  machinePassword: '',
  machinePasswordType: 'text',
  machinePasswordMasked: false,
  orderSource: '',
  isExternalDevice: false,
  productId: '',
  productName: '',
  brandName: '',
  color: '',
  imei: '',
  isChargeCustomer: true,
  // "Loại BH" = SP bảo hành (productType=5). warrantyPeriodMonths = máy + gói (BE tính, FE preview).
  warrantyProductId: '',
  warrantyProductName: '',
  warrantyStartDate: '',
  warrantyPeriodMonths: null,
  customerDescription: '',
  technicianNote: '',
  // R12: mỗi vai trò nhiều người (mảng userId)
  technicianIds: [],
  checkTechnicianIds: [],
  coordinatorIds: [],
  appointmentDate: '',
  // REQ-F5.5: ID master Loại trả — set khi → COMPLETED, hiển thị readonly trên phiếu.
  returnTypeId: null,
  status: '',
  // Decision #9 + M8: track returnedAt / serviceBillId / refundBillId
  returnedAt: null,
  // M7 V2: HĐ BÁN LẺ (RETAIL_INVOICE=1) auto-tạo khi returned + billNumber để navigate
  serviceBillId: null,
  serviceBillNumber: null,
  // M11 V2: HĐ BẢO HÀNH (WARRANTY=6) — set external từ module warranty-bill
  warrantyBillId: null,
  warrantyBillNumber: null,
  refundBillId: null,
  refundBillNumber: null,
  slipNumber: '',
  services: [],
  errorCodes: [],
});

export default {
  name: 'RepairSlipForm',
  mixins: [validationMixin],
  components: { KTCodePreview, AutoSuggest, FileUploadManager, Treeselect },
  data() {
    return {
      form: initialForm(),
      submitting: false,
      // Wizard pattern: instance + step hiện tại (1-based để khớp KTWizard)
      wizard: null,
      currentStep: 1,
      stores: [],
      // AutoSuggest store: search text + filter list. Sync với form.storeId qua onSelectStore.
      storeSearchText: '',
      filteredStoreOptions: [],
      repairInfoMaster: [],
      errorCodeMaster: [],
      // AutoSuggest search state cho mã lỗi & dịch vụ — pattern giống store/KTV.
      // Bỏ errorCodeToAdd/serviceToAdd (cũ — model b-form-select) sau khi chuyển sang typeahead.
      errorCodeSearchText: '',
      serviceSearchText: '',
      employeeOptions: [],
      filteredEmployeeOptions: [],
      technicianName: '',
      checkTechnicianName: '',
      coordinatorName: '',
      productOptions: [],
      searchProductText: '',
      productSearching: false,
      // Brand master cache — load 1 lần cho AutoSuggest "Hãng" (gõ tên → match).
      // SP có FK products.brand → lookup brandMap để auto-fill. SP chưa gán → user tự gõ + chọn.
      brandMaster: [],
      brandMap: {}, // { [id]: name } — accelerate ID-to-name lookup
      brandSearchText: '',
      filteredBrandOptions: [],
      // Địa chỉ KH: dropdown phụ thuộc Tỉnh/TP → Phường/Xã (Vietnam 2-tier reform).
      // Endpoint sẵn: GET city/ + GET city/:cityId/ward?effectiveType=1.
      cityOptions: [],
      wardOptions: [],
      // SP bảo hành (productType=5) = "loại BH". Tách tháng máy / tháng gói để hiện preview.
      warrantyProductOptions: [],
      searchWarrantyProductText: '',
      deviceWarrantyMonths: 0,
      warrantyProductMonths: 0,
      customerOptions: [],
      customerSearching: false,
      // M5: state machine
      transition: {
        toStatus: '',
        note: '',
        cancelReason: null,
        vendorPartner: '',
        diagnosisCharge: 0,
        // REQ-F5.5: chỉ điền khi toStatus = 'completed'
        returnTypeId: null,
        // R12: KTV kiểm tra nhiều người (mảng userId)
        checkTechnicianIds: [],
        technicianNote: '',
      },
      transitionSubmitting: false,
      // Flag báo đang auto-save nhân sự sau thêm/xoá KTV (UX feedback).
      techniciansSaving: false,
      // Lỗi inline hiện trong modal chuyển trạng thái — toast top-right hay bị modal che.
      transitionError: '',
      // REQ-F5.5: master Loại trả load 1 lần khi mount (3 dòng cứng).
      returnTypeOptions: [],
      // Map id (number) → code (return_ok | return_out | return_free) để phát hiện
      // case "không sửa được, trả máy" — cảnh báo + skip auto-tạo Bill bán lẻ.
      returnTypeCodeById: {},
      // KTV picker cho modal COMPLETED — chia sẻ dataset với input ngoài form.
      checkTechnicianModalSearch: '',
      // M5: history tab
      histories: [],
      loadingHistories: false,
      // Decision #9: undo returned
      undoReturnNote: '',
      undoReturnSubmitting: false,
      // M8: refund
      refund: initialRefund(),
      refundSubmitting: false,
      refundError: '',
      // Flag chặn watch `isInWarranty` overwrite `isChargeCustomer` khi đang load phiếu.
      isSlipLoaded: false,
      // M6: parts (linh kiện + SP dịch vụ)
      parts: [],
      partSearchOptions: [],
      searchPartText: '',
      partToAdd: null,
      addingPart: false,
      dpConfigs: DP_CONFIG,
    };
  },
  validations: {
    form: {
      storeId: { required },
      customerName: { required, maxLength: maxLength(255) },
      customerPhone: { required, maxLength: maxLength(20) },
      // REQ-F4.2 V2: bắt buộc chọn product từ master cho cả 2 luồng (DDV + ngoài)
      productId: { required },
    },
  },
  computed: {
    // Expose frozen constants cho template (không reactive-wrap mỗi instance).
    cancelReasonOptions: () => CANCEL_REASON_OPTIONS,
    orderSourceOptions: () => ORDER_SOURCE_OPTIONS,
    refundReasonOptions: () => REFUND_REASON_OPTIONS,
    historyFields: () => HISTORY_FIELDS,
    partFields: () => PART_FIELDS,
    errorCodeFields: () => ERROR_CODE_FIELDS,
    serviceFields: () => SERVICE_FIELDS,
    isEdit() {
      return !!this.$route.params.id;
    },
    /**
     * Tạo phiếu trực tiếp (không qua HĐ bảo hành) → chỉ dùng cho Hàng ngoài.
     * Hàng DDV bắt buộc tạo qua HĐ bảo hành (deep-link fromWarrantyBillNumber).
     */
    isDirectCreate() {
      return !this.isEdit && !this.$route.query.fromWarrantyBillNumber;
    },
    /**
     * Tạo phiếu từ HĐ bảo hành (deep-link) → máy bị khoá theo danh sách candidates của HĐ,
     * không cho search SP tự do ngoài hoá đơn.
     */
    isFromWarrantyBill() {
      return !this.isEdit && !!this.$route.query.fromWarrantyBillNumber;
    },
    /**
     * Phiếu gắn HĐ bảo hành (tạo từ HĐ BH hoặc đã liên kết khi sửa) → KHÓA: Loại hàng,
     * thiết bị (SP/màu/IMEI) và Ngày bắt đầu BH. Lý do: data lấy từ HĐ bảo hành, sửa sẽ
     * lệch nghiệp vụ — đặc biệt ngày BH ảnh hưởng số tháng BH → sai phí thu công sửa chữa.
     */
    isWarrantyLinked() {
      return this.isFromWarrantyBill || !!this.form.warrantyBillId;
    },
    pageTitle() {
      return this.isEdit ? 'Chi tiết phiếu sửa chữa' : 'Tạo phiếu sửa chữa mới';
    },
    readOnly() {
      return this.isEdit && this.form.status && this.form.status !== SLIP_STATUS.RECEIVING;
    },
    /**
     * Tài liệu được phép upload/xoá kể cả khi phiếu đã returned/cancelled
     * (bổ sung ảnh giao máy, biên bản...). Chỉ chặn khi user thiếu permission UPDATE.
     */
    canManageFiles() {
      return checkPermission('WARRANTY_SERVICE_UPDATE');
    },
    canSubmit() {
      if (this.isEdit) {
        return !this.readOnly && checkPermission('WARRANTY_SERVICE_UPDATE');
      }
      return checkPermission('WARRANTY_SERVICE_CREATE');
    },
    storeOptions() {
      return this.stores.map((s) => ({
        id: s.id,
        name: s.name,
        shortName: s.shortName,
        // displayName hiển thị "Tên (shortName)" — phân biệt cửa hàng cùng tên khu vực.
        displayName: s.shortName ? `${s.name} (${s.shortName})` : s.name,
      }));
    },
    // Radio loại mật khẩu — text (PIN/chữ-số) hoặc pattern (hình vẽ).
    passwordTypeOptions: () => ([
      { value: 'text', text: 'Kí tự' },
      { value: 'pattern', text: 'Hình' },
    ]),
    passwordPlaceholder() {
      if (this.form.machinePasswordMasked) return '••••••';
      return this.form.machinePasswordType === 'pattern'
        ? 'Mô tả hình vẽ (VD: 1→2→5→8→9)'
        : 'Gõ "Không có" nếu KH không cung cấp';
    },
    // Suggestion list cho AutoSuggest mã lỗi — filter theo text gõ + loại item đã thêm.
    filteredErrorCodeOptions() {
      const kw = removeAccents(String(this.errorCodeSearchText || '').toLowerCase());
      const added = new Set(this.form.errorCodes.map((s) => s.errorCodeId));
      return this.errorCodeMaster
        .filter((e) => !added.has(e.id))
        .map((e) => ({
          id: e.id,
          errorCode: e.errorCode,
          errorName: e.errorName,
          displayName: `${e.errorCode} - ${e.errorName}`,
        }))
        .filter((e) => {
          if (!kw) return true;
          const code = String(e.errorCode || '').toLowerCase();
          const name = removeAccents(String(e.errorName || '').toLowerCase());
          return code.indexOf(kw) > -1 || name.indexOf(kw) > -1;
        });
    },
    // Suggestion list cho AutoSuggest hạng mục dịch vụ — pattern y hệt mã lỗi.
    filteredServiceOptions() {
      const kw = removeAccents(String(this.serviceSearchText || '').toLowerCase());
      const added = new Set(this.form.services.map((s) => s.repairInfoId));
      return this.repairInfoMaster
        .filter((r) => !added.has(r.id))
        .map((r) => ({
          id: r.id,
          repairCode: r.repairCode,
          repairName: r.repairName,
          displayName: `${r.repairCode} - ${r.repairName}`,
        }))
        .filter((r) => {
          if (!kw) return true;
          const code = String(r.repairCode || '').toLowerCase();
          const name = removeAccents(String(r.repairName || '').toLowerCase());
          return code.indexOf(kw) > -1 || name.indexOf(kw) > -1;
        });
    },
    /**
     * Ngày hết BH tính từ start + period (không lưu mà compute realtime để theo thay đổi inline trên form).
     * Trả null nếu thiếu data — coi như "không xác định" → không lock isChargeCustomer.
     */
    warrantyEndComputed() {
      const start = this.form.warrantyStartDate;
      const months = Number(this.form.warrantyPeriodMonths);
      if (!start || !months) return null;
      // start có thể là DD/MM/YYYY (từ date-picker) hoặc ISO. moment auto-parse cả 2.
      const m = moment(start, ['DD/MM/YYYY', 'YYYY-MM-DD', moment.ISO_8601], true);
      if (!m.isValid()) return null;
      return m.add(months, 'months').startOf('day');
    },
    /**
     * Máy còn trong BH? Hàng ngoài (isExternalDevice=true) hoặc thiếu data → coi như hết BH
     * (cho phép thu phí). Confirm với user: hard lock không có override.
     */
    isInWarranty() {
      if (this.form.isExternalDevice) return false;
      const end = this.warrantyEndComputed;
      if (!end) return false;
      return moment().startOf('day').isBefore(end);
    },
    /**
     * Badge hiển thị cạnh section BH. Trả null nếu không có data BH (vd hàng ngoài).
     * @returns {null | {variant:string, text:string}}
     */
    warrantyStatusBadge() {
      const end = this.warrantyEndComputed;
      if (!end || this.form.isExternalDevice) return null;
      const endStr = end.format('DD/MM/YYYY');
      if (this.isInWarranty) {
        return { variant: 'success', text: `Còn BH đến ${endStr}` };
      }
      return { variant: 'danger', text: `Hết BH từ ${endStr}` };
    },
    canChangeStatus() {
      return checkPermission('WARRANTY_SERVICE_STATUS_CHANGE');
    },
    allowedNextStatuses() {
      // 2026-05-26: bỏ flow_group → next statuses chỉ theo bảng transition cơ bản.
      if (!this.form.status) return [];
      return STATE_TRANSITIONS[this.form.status] || [];
    },
    transitionRequiresNote() {
      return REQUIRES_NOTE.includes(this.transition.toStatus);
    },
    transitionRequiresVendor() {
      return REQUIRES_VENDOR.includes(this.transition.toStatus);
    },
    transitionRequiresCancelReason() {
      return REQUIRES_CANCEL_REASON.includes(this.transition.toStatus);
    },
    isTerminalChargeStatus() {
      return TERMINAL_CHARGE_STATUSES.includes(this.transition.toStatus);
    },
    // Phiếu đã chốt ở BER/KH từ chối báo giá + KTBH đã ghi nhận phí chẩn đoán.
    // Banner nhắc KTBH thu KH + tạo HĐ bán lẻ tay (REJECTED_QUOTE/BER không tự sinh Bill).
    showDiagnosisChargeBanner() {
      return TERMINAL_CHARGE_STATUSES.includes(this.form.status)
        && Number(this.form.diagnosisCharge) > 0;
    },
    // Yêu cầu hoàn tiền đã ghi nhận (action=refund trong history). Banner nhắc KTBH
    // thực hiện hoàn tiền thật cho KH qua phiếu thanh toán hoàn tiền (luồng độc lập),
    // vì refundSlip chỉ ghi metadata, KHÔNG tự tạo Bill REFUND / trừ tiền.
    refundRecord() {
      const row = this.histories.find((h) => h.actionType === HISTORY_ACTION.REFUND);
      if (!row) return null;
      let parsed = {};
      try {
        parsed = row.newValue ? JSON.parse(row.newValue) : {};
      } catch (e) {
        parsed = {};
      }
      const reasonOpt = REFUND_REASON_OPTIONS.find((o) => o.value === parsed.reason);
      return {
        amount: Number(parsed.amount) || 0,
        reason: parsed.reason || '',
        reasonLabel: (reasonOpt && reasonOpt.text) || parsed.reason || '',
        note: row.note || '',
        createdAt: row.createdAt,
      };
    },
    // REQ-F5.5: render extra fields trong modal khi → COMPLETED.
    isCompletedTransition() {
      return this.transition.toStatus === SLIP_STATUS.COMPLETED;
    },
    // True khi user chọn "Không sửa được, trả máy" trong modal COMPLETED.
    // Dùng để (a) cảnh báo nếu phiếu còn SP xuất hoá đơn, (b) chặn auto-tạo Bill bán lẻ.
    isReturnOutSelected() {
      const id = this.transition.returnTypeId;
      return !!id && this.returnTypeCodeById[id] === RETURN_TYPE_CODE.RETURN_OUT;
    },
    // Cảnh báo: chọn return_out mà phiếu vẫn còn SP xuất hoá đơn (linh kiện/dịch vụ có giá).
    showReturnOutWithPartsWarning() {
      return this.isCompletedTransition
        && this.isReturnOutSelected
        && this.parts.length > 0;
    },
    // Decision #9: undo returned
    canShowUndoReturn() {
      return (
        this.isEdit
        && this.form.status === SLIP_STATUS.RETURNED
        && checkPermission('WARRANTY_SERVICE_RETURNED_UNDO')
      );
    },
    undoReturnDisabledReason() {
      // Chỉ hiển thị reason khi nút disabled
      if (!this.form.returnedAt) {
        return 'Chưa có thời điểm trả máy';
      }
      const elapsed = Date.now() - new Date(this.form.returnedAt).getTime();
      const hours = Math.floor(elapsed / (60 * 60 * 1000));
      if (elapsed > UNDO_RETURN_WINDOW_MS) {
        return `Quá 24h (${hours}h) — không thể hoàn tác. Dùng Refund flow.`;
      }
      if (this.form.serviceBillId) {
        return 'Phiếu đã có hoá đơn — không thể hoàn tác. Dùng Refund flow.';
      }
      return 'Hoàn tác về trạng thái "Đã thông báo KH"';
    },
    canUndoReturn() {
      if (!this.form.returnedAt) return false;
      const elapsed = Date.now() - new Date(this.form.returnedAt).getTime();
      if (elapsed > UNDO_RETURN_WINDOW_MS) return false;
      if (this.form.serviceBillId) return false;
      return true;
    },
    // M8: refund
    canShowRefund() {
      return (
        this.isEdit
        && this.form.serviceBillId
        && checkPermission('WARRANTY_SERVICE_REFUND')
      );
    },
    canRefund() {
      return !!(this.form.serviceBillId && !this.form.refundBillId);
    },
    refundDisabledReason() {
      if (!this.form.serviceBillId) {
        return 'Phiếu chưa có hoá đơn bán lẻ — không thể ghi nhận hoàn tiền';
      }
      if (this.form.refundBillId) {
        return 'Phiếu đã có yêu cầu hoàn tiền trước đó';
      }
      return 'Ghi nhận yêu cầu hoàn tiền cho khách';
    },
    // M6: parts
    partReadOnly() {
      // Chỉ cho thao tác linh kiện ở các trạng thái thi công
      if (!this.isEdit || !this.form.status) return true;
      return !PART_EDITABLE_STATUSES.includes(this.form.status);
    },
    partsTotalServices() {
      return this.parts
        .filter((p) => Number(p.productType) === PRODUCT_TYPE.PRODUCT_SERVICE)
        .reduce((sum, p) => sum + this.computeLineTotal(p), 0);
    },
    partsTotalParts() {
      return this.parts
        .filter((p) => Number(p.productType) === PRODUCT_TYPE.PRODUCT)
        .reduce((sum, p) => sum + this.computeLineTotal(p), 0);
    },
    partsTotalCombos() {
      return this.parts
        .filter((p) => Number(p.productType) === PRODUCT_TYPE.PRODUCT_COMBO)
        .reduce((sum, p) => sum + this.computeLineTotal(p), 0);
    },
    partsTotalAll() {
      return this.partsTotalServices + this.partsTotalParts + this.partsTotalCombos;
    },
    /**
     * Tổng tiền linh kiện sẽ snapshot vào Hoá đơn bán lẻ (tạo ở bước Hoàn thành).
     * 2026-05-27: bỏ khái niệm miễn phí → mọi linh kiện có giá đều tính.
     */
    partsTotalBillable() {
      return this.parts
        .filter((p) => Number(p.price) > 0)
        .reduce((sum, p) => sum + this.computeLineTotal(p), 0);
    },
    /**
     * Có ít nhất 1 HĐ liên kết → hiện dropdown "HĐ liên kết".
     */
    hasLinkedBills() {
      return !!(this.form.serviceBillId || this.form.warrantyBillId || this.form.refundBillId);
    },
  },
  watch: {
    // Treeselect không có @change → dùng watcher: user đổi Tỉnh/TP → clear Phường/Xã +
    // reload list. Sau loadSlip BE trả cityId → fetch wards luôn (gọi từ loadSlip giữ giá trị ward đã lưu).
    'form.customerCityId'(newVal, oldVal) {
      // Bỏ qua lần load đầu (oldVal === undefined) — loadSlip tự xử lý fetch wards + giữ wardId hiện tại.
      if (oldVal === undefined) return;
      if (newVal !== oldVal) {
        this.form.customerWardId = null;
        this.fetchWardsByCity(newVal);
      }
    },
    'form.storeId'(newVal, oldVal) {
      if (newVal && newVal !== oldVal) {
        // Khi user đổi cửa hàng (không phải lần load đầu) → clear nhân viên đã chọn
        if (oldVal !== null && oldVal !== undefined) {
          this.form.technicianIds = [];
          this.form.checkTechnicianIds = [];
          this.form.coordinatorIds = [];
          this.technicianName = '';
          this.checkTechnicianName = '';
          this.coordinatorName = '';
        }
        this.fetchEmployees(newVal);
      }
    },
    // Auto-lock isChargeCustomer khi máy còn trong BH (REQ-F4.7 B2 — đã chốt: hard lock không override).
    // Linh kiện có giá vẫn có thể tạo Bill bán lẻ — guard isChargeCustomer ở redirect đã được bỏ.
    //
    // Gate bằng isSlipLoaded: tránh watch ghi đè giá trị BE vừa trả về trong loadSlip()
    // (vd: QL set isChargeCustomer=true cho case đặc biệt → reload phiếu không được auto-flip false).
    // Sau khi load xong, watch mới kick in cho thay đổi thực sự từ user (chỉnh ngày BH, đổi SP bảo hành).
    isInWarranty(val) {
      if (!this.isSlipLoaded && this.isEdit) return;
      if (val && this.form.isChargeCustomer !== false) {
        this.form.isChargeCustomer = false;
      }
    },
  },
  async created() {
    await Promise.all([
      this.fetchStores(),
      this.fetchRepairInfos(),
      this.fetchErrorCodes(),
      this.fetchReturnTypes(),
      this.fetchBrands(),
      this.fetchCities(),
    ]);
    if (this.isEdit) {
      // loadSlip phải xong trước (set form.status / form.id) — parts + histories độc lập.
      await this.loadSlip(this.$route.params.id);
      // Load sớm để banner refund (tab 1) hiển thị mà không cần user mở tab Lịch sử.
      await Promise.all([this.loadParts(), this.loadHistories()]);
    } else if (this.$route.query.fromWarrantyBillNumber) {
      // M11 V2: deep-link từ HĐ bảo hành → pre-fill form (Hàng DDV)
      await this.prefillFromWarrantyBill(this.$route.query.fromWarrantyBillNumber);
    } else {
      // Tạo trực tiếp → chỉ dành cho Hàng ngoài (Hàng DDV phải qua HĐ bảo hành)
      this.form.isExternalDevice = true;
    }
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: 'Dịch vụ bảo hành' },
      { title: 'Phiếu sửa chữa', route: '/warranty-service/repair-slips' },
      { title: this.isEdit ? 'Chi tiết phiếu' : 'Tạo phiếu mới' },
    ]);
    // Init wizard sau khi DOM render xong (giống AddOrder pattern)
    this.$nextTick(() => {
      this.wizard = new KTWizard('kt_wizard_repair_slip', {
        startStep: 1,
        clickableSteps: true,
      });
      // Khi user click sang step 5 (Lịch sử) → auto load history
      this.wizard.on('change', (wizardObj) => {
        this.currentStep = wizardObj.currentStep;
        if (this.isEdit && this.currentStep === 5) {
          this.loadHistories();
        }
      });
    });
  },
  beforeDestroy() {
    // Cleanup KTWizard: tránh leak event listener khi user back-forward giữa các phiếu.
    // KTWizard cũ không expose destroy() rõ ràng → clear ref để GC + remove DOM event nếu API có.
    if (this.wizard) {
      if (typeof this.wizard.destroy === 'function') {
        try { this.wizard.destroy(); } catch (e) { /* ignore */ }
      }
      this.wizard = null;
    }
    // Huỷ pending debounce để tránh fire callback sau khi component đã destroyed.
    [
      this.debouncedFetchCustomers,
      this.debouncedFetchProducts,
      this.debouncedFetchWarrantyProducts,
      this.debouncedFetchParts,
      this.onPartChanged,
    ].forEach((fn) => {
      if (fn && typeof fn.clear === 'function') fn.clear();
    });
  },
  methods: {
    async fetchStores() {
      try {
        const { data } = await ApiService.query(STORES_API);
        this.stores = (data && data.data) || [];
        this.filteredStoreOptions = [...this.storeOptions];
        // Nếu phiếu đã có storeId (edit / pre-selected) → hiển thị tên trong ô search.
        this.syncStoreSearchText();
      } catch (err) {
        // Bỏ qua, dropdown trống — user chọn không được sẽ báo lỗi khi submit
      }
    },
    // Đồng bộ `storeSearchText` từ form.storeId — gọi sau khi fetchStores + sau loadSlip.
    syncStoreSearchText() {
      if (!this.form.storeId) {
        this.storeSearchText = '';
        return;
      }
      const opt = this.storeOptions.find((s) => s.id === this.form.storeId);
      this.storeSearchText = opt ? opt.displayName : '';
    },
    onSearchStore(text) {
      this.storeSearchText = text;
      const kw = removeAccents(String(text || '').toLowerCase());
      // Filter theo cả name + shortName (giống pattern KTV name+code).
      this.filteredStoreOptions = this.storeOptions.filter((s) => {
        const name = removeAccents(String(s.name || '').toLowerCase());
        const shortName = removeAccents(String(s.shortName || '').toLowerCase());
        return name.indexOf(kw) > -1 || shortName.indexOf(kw) > -1;
      });
    },
    onSelectStore(option) {
      const item = option && option.item;
      if (!item) return;
      this.form.storeId = item.id;
      this.storeSearchText = item.displayName;
      this.$v.form.storeId.$touch();
    },
    // "Loại BH" = SP bảo hành (productType=5). Số tháng BH = tháng máy + tháng gói.
    recomputeWarrantyMonths() {
      this.form.warrantyPeriodMonths = (Number(this.deviceWarrantyMonths) || 0)
        + (Number(this.warrantyProductMonths) || 0);
    },
    debouncedFetchWarrantyProducts: debounce(function (text) {
      this.fetchWarrantyProducts(text);
    }, 350),
    onInputWarrantyProduct(text) {
      this.searchWarrantyProductText = text || '';
      // Xoá hết / sửa khác tên đã chọn → bỏ liên kết SP bảo hành + reset tháng gói.
      if (!text || (this.form.warrantyProductName && text.trim() !== this.form.warrantyProductName.trim())) {
        this.form.warrantyProductId = '';
        this.form.warrantyProductName = '';
        this.warrantyProductMonths = 0;
        this.recomputeWarrantyMonths();
      }
      const kw = (text || '').trim();
      if (kw.length < 2) {
        this.warrantyProductOptions = [];
        return;
      }
      this.debouncedFetchWarrantyProducts(kw);
    },
    async fetchWarrantyProducts(text) {
      this.productSearching = true;
      try {
        const { data } = await ApiService.query('productSearch/search-by-type', {
          params: { searchProduct: text.trim(), typeSearch: 2 },
        });
        // typeSearch=2 trả nhiều loại → lọc chỉ Sản phẩm bảo hành.
        this.warrantyProductOptions = ((data && data.data) || [])
          .filter((p) => Number(p.productType) === PRODUCT_TYPE.PRODUCT_WARRANTY);
      } catch (err) {
        this.warrantyProductOptions = [];
      } finally {
        this.productSearching = false;
      }
    },
    onSelectWarrantyProduct(option) {
      const item = option && option.item;
      if (!item) return;
      this.form.warrantyProductId = item.productId || item.id;
      this.form.warrantyProductName = item.productName;
      this.searchWarrantyProductText = item.productName;
      this.warrantyProductMonths = Number(item.warrantyMonthNo) || 0;
      this.recomputeWarrantyMonths();
    },
    async fetchRepairInfos() {
      try {
        const { data } = await ApiService.query(REPAIR_INFOS_API);
        this.repairInfoMaster = data.data || [];
      } catch (err) {
        // Bỏ qua
      }
    },
    async fetchErrorCodes() {
      try {
        const { data } = await ApiService.query(ERROR_CODES_API);
        this.errorCodeMaster = data.data || [];
      } catch (err) {
        // Bỏ qua
      }
    },
    async fetchEmployees(storeId) {
      if (!storeId) {
        this.employeeOptions = [];
        this.filteredEmployeeOptions = [];
        return;
      }
      try {
        const response = await ApiService.get(
          `employees/get-by-store?employeeName=&storeId=${storeId}`,
        );
        // Response shape: response.data = { status, message, data: [employees] }
        const employees = (response && response.data && response.data.data) || [];
        this.employeeOptions = employees.map((e) => ({
          id: e.id,
          name: e.fullName,
          code: e.code,
          // `displayName` để AutoSuggest dropdown hiển thị "Tên (MÃ NV)" — phân biệt trùng tên.
          displayName: e.code ? `${e.fullName} (${e.code})` : e.fullName,
        }));
        this.filteredEmployeeOptions = [...this.employeeOptions];
      } catch (err) {
        // Bỏ qua, dropdown trống
      }
    },
    // R12: tên nhân viên theo id (cho chip). Hiển thị "Tên (MÃ NV)" để phân biệt trùng tên
    // — pattern dùng nhất quán với form Bill / Transfer-Store. Fallback #id nếu chưa load danh sách.
    employeeName(id) {
      const emp = this.employeeOptions.find((e) => e.id === id);
      if (!emp) return `#${id}`;
      return emp.code ? `${emp.name} (${emp.code})` : emp.name;
    },
    // R12: lọc mảng technicians (bảng nối) theo vai trò → mảng userId.
    techIdsByRole(technicians, role) {
      if (!Array.isArray(technicians)) return [];
      return technicians.filter((t) => t.role === role).map((t) => t.userId);
    },
    // R12: bỏ 1 người khỏi vai trò (field = technicianIds | checkTechnicianIds | coordinatorIds).
    removeTechnician(field, id) {
      const isModal = field === 'modalCheckTechnicianIds';
      const target = isModal
        ? this.transition.checkTechnicianIds
        : this.form[field];
      const idx = target.indexOf(id);
      if (idx > -1) target.splice(idx, 1);
      // Auto-save khi đổi nhân sự trong form (không áp dụng cho modal transition).
      if (!isModal) this.debouncedPersistTechnicians();
    },
    /**
     * Auto-save danh sách nhân sự (3 vai trò) ngay khi user thêm/xoá — không cần ấn "Cập nhật".
     * Lý do: muốn chuyển trạng thái ngay sau khi gán KTV mà không phải submit cả form.
     * BE hỗ trợ partial update — gửi 3 mảng = thay toàn bộ vai trò tương ứng (undefined = giữ nguyên).
     * Debounce để tránh spam khi user gán nhiều người liên tiếp.
     */
    async persistTechnicians() {
      if (!this.isEdit || this.submitting) return;
      const id = this.$route.params.id;
      if (!id) return;
      this.techniciansSaving = true;
      try {
        await ApiService.put(API_BASE, {
          id,
          technicianIds: this.form.technicianIds || [],
          checkTechnicianIds: this.form.checkTechnicianIds || [],
          coordinatorIds: this.form.coordinatorIds || [],
        });
      } catch (err) {
        makeToastFaile(`Lưu nhân sự thất bại: ${extractError(err)}`);
      } finally {
        this.techniciansSaving = false;
      }
    },
    // R12: map "kind" của AutoSuggest → field mảng tương ứng (form hoặc transition modal).
    techFieldForKind(kind) {
      const map = {
        technician: { obj: 'form', field: 'technicianIds', search: 'technicianName' },
        checkTechnician: { obj: 'form', field: 'checkTechnicianIds', search: 'checkTechnicianName' },
        coordinator: { obj: 'form', field: 'coordinatorIds', search: 'coordinatorName' },
        modalCheckTechnician: { obj: 'transition', field: 'checkTechnicianIds', search: 'checkTechnicianModalSearch' },
      };
      return map[kind] || null;
    },
    onInputEmployee(text, kind) {
      const cfg = this.techFieldForKind(kind);
      if (cfg) this[cfg.search] = text;
      const kw = removeAccents(String(text || '').toLowerCase());
      // Lọc theo cả tên lẫn mã NV — user có thể gõ "NV001" để tìm nhanh.
      this.filteredEmployeeOptions = this.employeeOptions.filter((e) => {
        const name = removeAccents(String(e.name || '').toLowerCase());
        const code = String(e.code || '').toLowerCase();
        return name.indexOf(kw) > -1 || code.indexOf(kw) > -1;
      });
    },
    onSelectEmployee(option, kind) {
      const item = option && option.item;
      if (!item) return;
      const cfg = this.techFieldForKind(kind);
      if (!cfg) return;
      // Thêm vào mảng (dedupe). Sau khi thêm → clear ô tìm để gõ tiếp người khác.
      const target = cfg.obj === 'transition' ? this.transition[cfg.field] : this.form[cfg.field];
      const wasAdded = !target.includes(item.id);
      if (wasAdded) target.push(item.id);
      this[cfg.search] = '';
      // Auto-save khi đổi nhân sự trong form chính. Modal transition gửi atomically cùng change-status.
      if (wasAdded && cfg.obj === 'form') {
        this.debouncedPersistTechnicians();
      }
    },

    // ===== Customer search by phone (pattern Bill / Order) =====
    debouncedFetchCustomers: debounce(function (text) {
      this.fetchCustomersByPhone(text);
    }, 500),
    // Auto-save nhân sự (debounce 400ms) — tránh spam khi user gán/xoá nhanh liên tiếp.
    debouncedPersistTechnicians: debounce(function () {
      this.persistTechnicians();
    }, 400),
    onSearchCustomer(text) {
      const value = text || '';
      // Khi user gõ thay đổi SĐT → bỏ liên kết KH cũ + reset Vuelidate dirty
      if (this.form.customerId && value !== this.form.customerPhone) {
        this.form.customerId = null;
      }
      this.form.customerPhone = value;
      this.$v.form.customerPhone.$touch();
      const kw = value.trim();
      if (kw.length < 2) {
        this.customerOptions = [];
        return;
      }
      this.debouncedFetchCustomers(kw);
    },
    onSelectCustomer(option) {
      const item = option && option.item;
      if (!item) return;
      // Item "không có trong hệ thống" có notFound flag → chỉ giữ phone, để user nhập tên
      if (item.notFound || !item.id) {
        this.form.customerPhone = item.phoneNo || this.form.customerPhone;
        this.form.customerId = null;
        return;
      }
      this.form.customerId = item.id;
      this.form.customerPhone = item.phoneNo || '';
      this.form.customerName = item.fullName || this.form.customerName || '';
      this.form.customerEmail = item.email || this.form.customerEmail || '';
      this.form.customerAddress = item.address || this.form.customerAddress || '';
      this.$v.form.customerName.$touch();
      this.$v.form.customerPhone.$touch();
    },
    async fetchCustomersByPhone(text) {
      const kw = (text || '').trim();
      if (!kw) {
        this.customerOptions = [];
        return;
      }
      this.customerSearching = true;
      try {
        const { data } = await ApiService.query('customer/get-by-phone', {
          params: { phoneNumber: kw },
        });
        const list = (data && data.data) || [];
        if (list.length > 0) {
          this.customerOptions = list;
        } else {
          // Hiển thị 1 dòng "không có trong hệ thống" để user thấy
          this.customerOptions = [
            { message: 'SĐT chưa có trong hệ thống — sẽ tạo KH mới', phoneNo: kw, notFound: true },
          ];
        }
      } catch (err) {
        this.customerOptions = [];
      } finally {
        this.customerSearching = false;
      }
    },

    debouncedFetchProducts: debounce(function (text) {
      this.fetchProducts(text);
    }, 350),
    onInputProduct(text) {
      this.searchProductText = text || '';
      // Khi user xoá hết text hoặc edit lại → bỏ liên kết productId
      if (!text || (this.form.productName && text.trim() !== this.form.productName.trim())) {
        this.form.productId = '';
      }
      this.form.productName = text || '';
      const kw = (text || '').trim();
      if (kw.length < 2) {
        this.productOptions = [];
        return;
      }
      this.debouncedFetchProducts(kw);
    },
    async fetchProducts(text) {
      if (!text || !text.trim()) {
        this.productOptions = [];
        return;
      }
      this.productSearching = true;
      try {
        const { data } = await ApiService.query('productSearch/search-by-type', {
          params: {
            searchProduct: text.trim(),
            typeSearch: 2, // 2 = PRODUCT + PRODUCT_SERVICE + PRODUCT_COMBO + PRODUCT_IMEI + PRODUCT_WARRANTY
          },
        });
        this.productOptions = (data && data.data) || [];
      } catch (err) {
        this.productOptions = [];
      } finally {
        this.productSearching = false;
      }
    },
    /**
     * Auto-fill warrantyStartDate từ ngày bán Bill gốc.
     * KHÔNG ghi đè nếu form đã có giá trị (KTV đã gõ tay) — tôn trọng input của user.
     */
    async applyImeiSaleSnapshot(imei) {
      try {
        const { data } = await ApiService.query(
          `${API_BASE}/imei-sale-info`,
          { params: { imei } },
        );
        const info = (data && data.data) || {};
        if (info.found && info.soldAt && !this.form.warrantyStartDate) {
          this.form.warrantyStartDate = fromIsoDate(info.soldAt);
        }
      } catch (err) {
        // Best-effort: không alert, không block — KTV tự gõ ngày mua
      }
    },
    async applyProductSnapshot(product) {
      // Gọi từ prefillFromWarrantyBill: input chỉ chắc chắn có id + productName.
      // Brand/color là FK (UUID/INT) → cần gọi product/{id} để lấy tên đã resolve.
      if (!product) return;
      const productId = product.id || product.productId || '';
      this.form.productId = productId;
      this.form.productName = product.productName || product.name || '';
      this.searchProductText = this.form.productName;
      // Số tháng BH của MÁY (productType=2) → cộng với tháng gói BH ở recomputeWarrantyMonths.
      if (product.warrantyMonthNo !== undefined && product.warrantyMonthNo !== null) {
        this.deviceWarrantyMonths = Number(product.warrantyMonthNo) || 0;
        this.recomputeWarrantyMonths();
      }
      // Lấy detail SP. Hãng = brandMap[detail.brand] (lookup local từ brand master).
      // productCateName là DANH MỤC (vd "Củ Sạc 1 cổng") — KHÔNG dùng.
      // SP chưa gán brand → giữ form.brandName cũ; user gõ + chọn từ AutoSuggest bên dưới.
      if (productId) {
        try {
          const { data } = await ApiService.query(`product/${productId}`);
          const detail = (data && data.data) || {};
          const brand = detail.brand && this.brandMap[detail.brand]
            ? this.brandMap[detail.brand]
            : '';
          if (brand) {
            this.form.brandName = brand;
            this.syncBrandSearchText();
          }
          if (Array.isArray(detail.listColor) && detail.listColor.length > 0) {
            this.form.color = detail.listColor[0].name || this.form.color;
          }
          if (!this.deviceWarrantyMonths
            && detail.warrantyMonthNo !== undefined
            && detail.warrantyMonthNo !== null
          ) {
            this.deviceWarrantyMonths = Number(detail.warrantyMonthNo) || 0;
            this.recomputeWarrantyMonths();
          }
        } catch (err) {
          // Không lấy được detail thì giữ nguyên productId+productName, user có thể tự sửa hãng/màu
        }
      }
    },
    async onSelectProduct(option) {
      const item = option && option.item;
      if (!item) return;
      this.form.productId = item.productId;
      this.form.productName = item.productName;
      this.searchProductText = item.productName;
      // Auto-fill hãng / màu / số tháng BH từ chi tiết SP.
      // Hãng: lookup brandMap theo FK products.brand. productCateName = danh mục → bỏ.
      try {
        const { data } = await ApiService.query(`product/${item.productId}`);
        const detail = (data && data.data) || {};
        const brand = detail.brand && this.brandMap[detail.brand]
          ? this.brandMap[detail.brand]
          : '';
        if (brand) {
          this.form.brandName = brand;
          this.syncBrandSearchText();
        }
        // Màu: listColor là mảng [{ id, name }]
        if (Array.isArray(detail.listColor) && detail.listColor.length > 0) {
          this.form.color = detail.listColor[0].name || '';
        }
        // Số tháng BH của MÁY (productType=2) → recompute = máy + gói.
        if (detail.warrantyMonthNo !== undefined && detail.warrantyMonthNo !== null) {
          this.deviceWarrantyMonths = Number(detail.warrantyMonthNo) || 0;
          this.recomputeWarrantyMonths();
        }
      } catch (err) {
        // Không có detail cũng không sao, vẫn giữ link productId+productName
      }
    },
    /**
     * M11 V2: pre-fill form tạo phiếu SC từ HĐ bảo hành.
     * Gọi BE eligibility endpoint → nếu OK, fill customer + product snapshot vào form.
     * Snapshot cũng include warrantyBillId để khi submit BE auto-link 2 chiều.
     */
    async prefillFromWarrantyBill(billNumber) {
      try {
        const { data } = await ApiService.query(
          `${API_BASE}/eligibility/from-warranty-bill`,
          { params: { billNumber } },
        );
        const result = (data && data.data) || {};
        if (!result.ok) {
          makeToastFaile(
            result.reason || 'HĐ bảo hành không đủ điều kiện tạo phiếu sửa chữa.',
          );
          // Quay về trang trước để user khỏi loay hoay với form trống
          this.$router.go(-1);
          return;
        }
        const snap = result.snapshot || {};
        this.form = {
          ...this.form,
          storeId: snap.storeId || this.form.storeId,
          customerId: snap.customerId,
          customerName: snap.customerName || '',
          customerPhone: snap.customerPhone || '',
          customerAddress: snap.customerAddress || '',
          warrantyBillId: snap.warrantyBillId, // BE sẽ dùng để link Bills.repairSlipId 2 chiều
          productId: snap.productId || null,
          productName: snap.productName || '',
          brandName: snap.brandName || '',
          imei: snap.imei || '',
        };
        this.searchProductText = snap.productName || '';
        this.syncBrandSearchText();
        // Decision 1.30: PBH eligible ↔ chỉ có 1 máy IMEI → fill thẳng, không qua modal.
        if (snap.productId) {
          await this.applyProductSnapshot({
            id: snap.productId,
            productName: snap.productName,
          });
        }
        if (snap.imei) {
          await this.applyImeiSaleSnapshot(snap.imei);
        }
        makeToastSuccess(
          `Đã pre-fill data từ HĐ bảo hành #${snap.warrantyBillNumber}.`,
        );
      } catch (err) {
        makeToastFaile(extractError(err));
      }
    },

    async loadSlip(id) {
      try {
        const { data } = await ApiService.query(`${API_BASE}/${id}`);
        const slip = data.data || {};
        this.form = {
          ...initialForm(),
          ...slip,
          isExternalDevice: !!slip.isExternalDevice,
          isChargeCustomer: slip.isChargeCustomer !== false,
          // BE trả machinePassword = '••••••' + machinePasswordMasked=true nếu user không có quyền xem.
          // FE clear field về '' khi masked để placeholder hiện đúng và tránh bind chuỗi mask vào v-model.
          machinePassword: slip.machinePasswordMasked ? '' : (slip.machinePassword || ''),
          machinePasswordType: slip.machinePasswordType || 'text',
          machinePasswordMasked: !!slip.machinePasswordMasked,
          // BE trả ISO YYYY-MM-DD → convert DD/MM/YYYY để date-picker hiển thị đúng
          warrantyStartDate: fromIsoDate(slip.warrantyStartDate),
          appointmentDate: fromIsoDate(slip.appointmentDate),
          // M7 V2 + M11 V2: flatten billNumber từ nested objects để badge click navigate được
          serviceBillNumber: slip.serviceBill ? slip.serviceBill.billNumber : null,
          warrantyBillNumber: slip.warrantyBill ? slip.warrantyBill.billNumber : null,
          refundBillNumber: slip.refundBill ? slip.refundBill.billNumber : null,
          services: (slip.services || []).map((s) => ({
            repairInfoId: s.repairInfoId,
            repairCode: s.repairCode,
            repairName: s.repairName,
            note: s.note || '',
          })),
          errorCodes: (slip.errorCodes || []).map((e) => ({
            errorCodeId: e.errorCodeId,
            errorCode: e.errorCode,
            errorName: e.errorName,
          })),
          // R12: tách mảng technicians (bảng nối) theo vai trò → 3 mảng id
          technicianIds: this.techIdsByRole(slip.technicians, 'main'),
          checkTechnicianIds: this.techIdsByRole(slip.technicians, 'check'),
          coordinatorIds: this.techIdsByRole(slip.technicians, 'coordinator'),
        };
        // Sync text input của AutoSuggest
        this.searchProductText = this.form.productName || '';
        this.searchWarrantyProductText = this.form.warrantyProductName || '';
        this.syncStoreSearchText();
        this.syncBrandSearchText();
        // Phiếu có sẵn cityId → load wardOptions để dropdown hiển thị đúng tên ward đã chọn.
        if (this.form.customerCityId) {
          await this.fetchWardsByCity(this.form.customerCityId);
        }
      } catch (err) {
        makeToastFaile(extractError(err));
      } finally {
        // Mở khoá watch isInWarranty sau khi BE state đã apply xong.
        this.isSlipLoaded = true;
      }
    },

    onSearchErrorCode(text) {
      this.errorCodeSearchText = text || '';
    },
    onSelectErrorCode(option) {
      const item = option && option.item;
      if (!item) return;
      this.form.errorCodes.push({
        errorCodeId: item.id,
        errorCode: item.errorCode,
        errorName: item.errorName,
      });
      this.errorCodeSearchText = '';
    },
    removeErrorCode(index) {
      this.form.errorCodes.splice(index, 1);
    },
    onSearchService(text) {
      this.serviceSearchText = text || '';
    },
    onSelectService(option) {
      const item = option && option.item;
      if (!item) return;
      this.form.services.push({
        repairInfoId: item.id,
        repairCode: item.repairCode,
        repairName: item.repairName,
        note: '',
      });
      this.serviceSearchText = '';
    },
    removeService(index) {
      this.form.services.splice(index, 1);
    },

    validateState(name) {
      const { $dirty, $error } = this.$v.form[name];
      return $dirty ? !$error : null;
    },

    async onSubmit() {
      this.$v.form.$touch();
      if (this.$v.form.$anyError) {
        makeToastFaile('Vui lòng điền đủ thông tin bắt buộc.');
        this.goToStep(1);
        return;
      }
      // REQ-F4.2 V2: bắt buộc liên kết product master cho CẢ 2 luồng
      if (!this.form.productId) {
        makeToastFaile(
          'Sản phẩm không tồn tại trong hệ thống. Vui lòng tạo sản phẩm trước rồi chọn từ danh mục.',
        );
        this.goToStep(1);
        return;
      }
      this.submitting = true;
      try {
        const payload = this.buildPayload();
        if (this.isEdit) {
          await ApiService.put(API_BASE, payload);
          makeToastSuccess('Cập nhật phiếu sửa chữa thành công');
        } else {
          const { data } = await ApiService.post(API_BASE, payload);
          makeToastSuccess(
            `Tạo phiếu thành công: ${data?.data?.slipNumber || ''}`,
          );
          this.$router.push({ name: 'list-repair-slips' });
        }
      } catch (err) {
        makeToastFaile(extractError(err));
      } finally {
        this.submitting = false;
      }
    },
    buildPayload() {
      const base = {
        storeId: this.form.storeId,
        customerName: this.form.customerName,
        customerPhone: this.form.customerPhone,
        customerAddress: this.form.customerAddress || null,
        customerCityId: this.form.customerCityId || null,
        customerWardId: this.form.customerWardId || null,
        customerEmail: this.form.customerEmail || null,
        // Khi PIN đang masked (user không có quyền xem) → KHÔNG gửi field này,
        // để BE giữ nguyên giá trị cũ thay vì ghi đè bằng chuỗi rỗng/'••••••'.
        ...(this.form.machinePasswordMasked
          ? {}
          : { machinePassword: this.form.machinePassword || null }),
        machinePasswordType: this.form.machinePasswordType || 'text',
        orderSource: this.form.orderSource || null,
        isExternalDevice: !!this.form.isExternalDevice,
        productId: this.form.productId || null,
        productName: this.form.productName || null,
        brandName: this.form.brandName || null,
        color: this.form.color || null,
        imei: this.form.imei || null,
        isChargeCustomer: this.form.isChargeCustomer !== false,
        // "Loại BH" = SP bảo hành (productType=5). warrantyPeriodMonths do BE tự tính (máy + gói).
        warrantyProductId: this.form.warrantyProductId || null,
        // Date-picker dùng format DD/MM/YYYY → convert ISO YYYY-MM-DD trước khi gửi BE
        warrantyStartDate: toIsoDate(this.form.warrantyStartDate),
        customerDescription: this.form.customerDescription || null,
        technicianNote: this.form.technicianNote || null,
        // R12: gửi mảng userId theo từng vai trò (bảng nối)
        technicianIds: this.form.technicianIds || [],
        checkTechnicianIds: this.form.checkTechnicianIds || [],
        coordinatorIds: this.form.coordinatorIds || [],
        appointmentDate: toIsoDate(this.form.appointmentDate),
        services: this.form.services,
        errorCodes: this.form.errorCodes,
        // M11 V2: chỉ gửi warrantyBillId khi CREATE từ HĐ BH (deep-link).
        // Update không cho thay đổi qua endpoint này — phải dùng /link-warranty-bill riêng.
        warrantyBillId: this.isEdit ? undefined : (this.form.warrantyBillId || null),
      };
      if (this.isEdit) {
        return { id: this.$route.params.id, ...base };
      }
      return base;
    },

    statusLabel(value) {
      const opt = STATUS_OPTIONS.find((o) => o.value === value);
      return opt ? opt.text : value;
    },
    statusVariant(value) {
      const opt = STATUS_OPTIONS.find((o) => o.value === value);
      return opt ? opt.variant : 'secondary';
    },

    formatDateTime(value) {
      return formatDateTime(value, true);
    },
    historyActionLabel(action) {
      const opt = HISTORY_ACTION_LABELS[action];
      return opt ? opt.text : action;
    },
    historyActionVariant(action) {
      const opt = HISTORY_ACTION_LABELS[action];
      return opt ? opt.variant : 'secondary';
    },

    // ============== M5: State machine ==============
    openTransitionModal(toStatus) {
      // REQ-F5.5: prefill từ form để KTV chỉ cần xác nhận; vẫn cho sửa trực tiếp ở modal.
      // R12: prefill KTV kiểm tra từ form (mảng), cho thêm/bớt trong modal.
      this.transition = {
        ...initialTransition(),
        toStatus,
        vendorPartner: this.form.vendorPartner || '',
        returnTypeId: this.form.returnTypeId || null,
        checkTechnicianIds: [...(this.form.checkTechnicianIds || [])],
        technicianNote: this.form.technicianNote || '',
      };
      this.checkTechnicianModalSearch = '';
      this.transitionError = '';
      this.$bvModal.show('modal-transition');
    },
    resetTransition() {
      this.transition = initialTransition();
      this.checkTechnicianModalSearch = '';
      this.transitionError = '';
    },
    async fetchReturnTypes() {
      if (this.returnTypeOptions.length > 0) return;
      try {
        const { data } = await ApiService.query(`${API_BASE}/return-types`);
        const list = (data && data.data) || [];
        this.returnTypeOptions = [
          { value: null, text: '— Chọn loại trả —' },
          ...list.map((r) => ({ value: r.id, text: r.name })),
        ];
        this.returnTypeCodeById = list.reduce((acc, r) => {
          acc[r.id] = r.code;
          return acc;
        }, {});
      } catch (err) {
        makeToastFaile('Không tải được danh sách Loại trả.');
      }
    },
    /**
     * Load brand master (1 lần) — cache để:
     *   1. Lookup tên hãng từ FK `products.brand` khi user chọn SP (auto-fill).
     *   2. Render dropdown cho user pick nếu SP chưa gán brand (productCateName ≠ Brand).
     * Pattern lấy giống UpdateProduct.vue — pageSize=1000 quá đủ cho master nhỏ.
     */
    async fetchBrands() {
      try {
        const paramQuery = { params: { param: { page: 1, pageSize: 1000, name: '' } } };
        const { data } = await ApiService.query('brands', paramQuery);
        this.brandMaster = (data && data.brands) || [];
        this.brandMap = this.brandMaster.reduce((acc, b) => {
          if (b && b.id) acc[b.id] = String(b.name || '').trim();
          return acc;
        }, {});
        this.filteredBrandOptions = [...this.brandMaster];
      } catch (err) {
        // Không tải được → AutoSuggest empty list. User vẫn có thể gõ tay (free text vẫn bind form.brandName).
      }
    },
    /**
     * Load danh sách Tỉnh/TP cho Treeselect (Vietnam 2-tier reform 2025-07).
     * Format: { id, label, custom } — `custom` = removeAccents để treeselect match-keys
     * tìm được cả khi user gõ không dấu (vd "ha noi" → "Hà Nội").
     * Endpoint: GET /city/ với effectiveType=AFTER_CHANGE (2-tier mới).
     */
    async fetchCities() {
      try {
        const { data } = await ApiService.query('city/', {
          params: { effectiveType: 2 },
        });
        const cities = (data && data.data) || [];
        this.cityOptions = cities.map((c) => ({
          id: c.id,
          label: c.name,
          custom: removeAccents(String(c.name || '')),
        }));
      } catch (err) {
        this.cityOptions = [];
      }
    },
    /**
     * Load Phường/Xã trực tiếp theo cityId (Vietnam 2-tier reform post-2025-07).
     * EFFECTIVE_TYPE.AFTER_CHANGE = 2; AFTER_CHANGE = 2 → BE filter Ward.cityId.
     * @param {number|null} cityId
     */
    async fetchWardsByCity(cityId) {
      if (!cityId) {
        this.wardOptions = [];
        return;
      }
      try {
        const { data } = await ApiService.query(`city/${cityId}/ward`, {
          params: { effectiveType: 2 },
        });
        const wards = (data && data.data) || [];
        this.wardOptions = wards.map((w) => ({
          id: w.id,
          label: w.name,
          custom: removeAccents(String(w.name || '')),
        }));
      } catch (err) {
        this.wardOptions = [];
      }
    },
    /**
     * AutoSuggest "Hãng" — pattern giống Cửa hàng/Sản phẩm: gõ filter + chọn.
     * Cho phép free text (bind text → form.brandName) để xử lý SP chưa gán brand
     * và snapshot legacy không khớp brand master mới.
     */
    onSearchBrand(text) {
      this.brandSearchText = text || '';
      this.form.brandName = this.brandSearchText.trim();
      const kw = removeAccents(this.brandSearchText.toLowerCase());
      if (!kw) {
        this.filteredBrandOptions = [...this.brandMaster];
        return;
      }
      this.filteredBrandOptions = this.brandMaster.filter((b) => {
        const name = removeAccents(String(b.name || '').toLowerCase());
        return name.indexOf(kw) > -1;
      });
    },
    onSelectBrand(option) {
      const item = option && option.item;
      if (!item) return;
      this.form.brandName = item.name;
      this.brandSearchText = item.name;
    },
    /**
     * Đồng bộ brandSearchText từ form.brandName — gọi sau loadSlip + sau auto-fill từ SP.
     * AutoSuggest hiển thị giá trị qua model prop (text), nên cần manual sync.
     */
    syncBrandSearchText() {
      this.brandSearchText = this.form.brandName || '';
    },
    async confirmTransition() {
      // IMEI/Serial bắt buộc khi rời "Tiếp nhận" — sau đó field bị khoá readOnly, không sửa được.
      if (this.form.status === SLIP_STATUS.RECEIVING
        && !String(this.form.imei || '').trim()) {
        const msg = 'Phải nhập IMEI/Serial của máy trước khi chuyển trạng thái — '
          + 'sau bước "Tiếp nhận", thiết bị sẽ bị khoá không sửa được.';
        this.transitionError = msg;
        makeToastFaile(msg);
        return;
      }
      if (this.transitionRequiresNote && !this.transition.note) {
        makeToastFaile('Trạng thái này bắt buộc nhập ghi chú.');
        return;
      }
      if (this.transitionRequiresVendor && !this.transition.vendorPartner) {
        makeToastFaile('Trạng thái "Gửi hãng" bắt buộc nhập tên Đối tác/Hãng.');
        return;
      }
      if (this.transitionRequiresCancelReason && !this.transition.cancelReason) {
        makeToastFaile('Vui lòng chọn lý do huỷ.');
        return;
      }
      // REQ-F5.5: 3 trường bắt buộc khi → COMPLETED. BE cũng enforce (double-validate cho UX).
      if (this.isCompletedTransition) {
        if (!this.transition.returnTypeId) {
          makeToastFaile('Vui lòng chọn "Loại trả".');
          return;
        }
        if (!this.transition.checkTechnicianIds || this.transition.checkTechnicianIds.length === 0) {
          makeToastFaile('Vui lòng chọn "KTV kiểm tra".');
          return;
        }
        if (!this.transition.technicianNote) {
          makeToastFaile('Vui lòng nhập "Ghi chú KTV" mô tả công việc đã sửa.');
          return;
        }
      }
      this.transitionSubmitting = true;
      this.transitionError = '';
      try {
        const payload = {
          toStatus: this.transition.toStatus,
          note: this.transition.note || null,
          cancelReason: this.transition.cancelReason || null,
          vendorPartner: this.transition.vendorPartner || null,
          diagnosisCharge: this.transition.diagnosisCharge || 0,
          // REQ-F5.5: gửi atomically cùng status change để tránh race save-form vs change-status.
          ...(this.isCompletedTransition
            ? {
              returnTypeId: this.transition.returnTypeId,
              checkTechnicianIds: this.transition.checkTechnicianIds,
              technicianNote: this.transition.technicianNote,
            }
            : {}),
        };
        const res = await ApiService.patch(
          `${API_BASE}/${this.$route.params.id}/status`,
          payload,
        );
        const toStatus = this.transition.toStatus;
        const successMsg = (res && res.data && res.data.message)
          || `Đã chuyển sang "${this.statusLabel(toStatus)}"`;
        makeToastSuccess(successMsg);
        this.form.status = toStatus;
        this.$bvModal.hide('modal-transition');
        // 2026-05-27: khi chuyển sang "Hoàn thành" (completed) → redirect sang form Hoá đơn
        // bán lẻ với fromRepairSlipId để KTBH chọn thanh toán + submit Bill (trừ kho XKBL cho
        // toàn bộ linh kiện). Trước đây ở "Đã trả máy"; chuyển lên completed theo nghiệp vụ mới.
        // 2026-06-04: skip redirect khi loại trả = return_out (không sửa được, trả máy) —
        // nghiệp vụ không thu phí dù phiếu còn parts; modal đã cảnh báo người dùng phía trên.
        if (
          toStatus === SLIP_STATUS.COMPLETED
          && this.partsTotalBillable > 0
          && !this.isReturnOutSelected
        ) {
          this.$router.push({
            name: 'add-bill',
            query: { fromRepairSlipId: this.$route.params.id },
          });
          return;
        }
        // Reload phiếu để cập nhật timestamps + vendorPartner + cancelReason
        await this.loadSlip(this.$route.params.id);
        await this.loadParts();
        // Nếu đang mở Tab lịch sử thì refresh
        if (this.currentStep === 5) {
          this.loadHistories();
        }
      } catch (err) {
        // Hiển thị inline trong modal (toast top-right thường bị modal/backdrop che) + giữ toast làm backup.
        const message = extractError(err);
        this.transitionError = message;
        makeToastFaile(message);
      } finally {
        this.transitionSubmitting = false;
      }
    },
    // M8: Refund flow
    openRefundModal() {
      this.refund = initialRefund();
      this.refundError = '';
      this.$bvModal.show('modal-refund');
    },
    resetRefund() {
      this.refund = initialRefund();
      this.refundError = '';
    },
    async confirmRefund() {
      this.refundError = '';
      const amount = Number(this.refund.amount) || 0;
      if (amount <= 0) {
        this.refundError = 'Số tiền hoàn phải > 0.';
        return;
      }
      if (!this.refund.reason) {
        this.refundError = 'Vui lòng chọn lý do hoàn tiền.';
        return;
      }
      this.refundSubmitting = true;
      try {
        await ApiService.post(
          `${API_BASE}/${this.$route.params.id}/refund`,
          {
            amount,
            reason: this.refund.reason,
            note: this.refund.note || null,
          },
        );
        makeToastSuccess(
          `Đã ghi nhận yêu cầu hoàn tiền ${formatPrice(amount)} đ. `
          + 'Vui lòng tạo phiếu thanh toán hoàn tiền thủ công để xử lý cho khách.',
        );
        this.$bvModal.hide('modal-refund');
        await this.loadSlip(this.$route.params.id);
        // Reload histories để banner refund + tab Lịch sử cập nhật ngay.
        this.loadHistories();
      } catch (err) {
        // Inline trong modal (toast top-right thường bị modal/backdrop che) + giữ toast làm backup.
        const message = extractError(err);
        this.refundError = message;
        makeToastFaile(message);
      } finally {
        this.refundSubmitting = false;
      }
    },

    // Decision #9: undo returned → notified
    openUndoReturnModal() {
      this.undoReturnNote = '';
      this.$bvModal.show('modal-undo-return');
    },
    async confirmUndoReturn() {
      const note = (this.undoReturnNote || '').trim();
      if (note.length < UNDO_RETURN_MIN_NOTE_LEN) {
        makeToastFaile(`Lý do hoàn tác tối thiểu ${UNDO_RETURN_MIN_NOTE_LEN} ký tự.`);
        return;
      }
      this.undoReturnSubmitting = true;
      try {
        await ApiService.patch(
          `${API_BASE}/${this.$route.params.id}/undo-return`,
          { note },
        );
        makeToastSuccess('Đã hoàn tác — phiếu trở về "Đã thông báo KH"');
        this.$bvModal.hide('modal-undo-return');
        await this.loadSlip(this.$route.params.id);
        await this.loadParts();
        if (this.currentStep === 5) this.loadHistories();
      } catch (err) {
        makeToastFaile(extractError(err));
      } finally {
        this.undoReturnSubmitting = false;
      }
    },

    async loadHistories() {
      if (!this.isEdit) return;
      this.loadingHistories = true;
      try {
        const { data } = await ApiService.query(
          `${API_BASE}/${this.$route.params.id}/histories`,
        );
        this.histories = (data && data.data) || [];
      } catch (err) {
        makeToastFaile(extractError(err));
      } finally {
        this.loadingHistories = false;
      }
    },
    parseRefundAmount(rawJson) {
      try {
        const parsed = rawJson ? JSON.parse(rawJson) : {};
        return Number(parsed.amount) || 0;
      } catch (e) {
        return 0;
      }
    },
    parseRefundReason(rawJson) {
      try {
        const parsed = rawJson ? JSON.parse(rawJson) : {};
        const opt = REFUND_REASON_OPTIONS.find((o) => o.value === parsed.reason);
        return (opt && opt.text) || parsed.reason || '';
      } catch (e) {
        return '';
      }
    },
    // ===== Phase 1 audit-log helpers (CREATE snapshot + UPDATE diff) =====
    /**
     * Parse newValue JSON-string của history record. Trả null khi không phải JSON
     * (vd record cũ lưu plain text như linkServiceBill ghi raw UUID).
     * @param {string|null} raw
     * @returns {Object|null}
     */
    parseHistoryValue(raw) {
      if (raw === null || raw === undefined || raw === '') return null;
      if (typeof raw !== 'string') return raw;
      try { return JSON.parse(raw); } catch (e) { return null; }
    },
    /**
     * Format 1 giá trị từ JSON log để hiển thị (date / boolean / long-text / null).
     * Giữ ISO date raw trong JSON, format DD/MM/YYYY khi hiển thị.
     */
    formatHistoryFieldValue(value) {
      if (value === null || value === undefined || value === '') return '(trống)';
      if (typeof value === 'boolean') return value ? 'Có' : 'Không';
      if (typeof value === 'string') {
        // ISO date: YYYY-MM-DD hoặc YYYY-MM-DDTHH:mm:ss
        if (/^\d{4}-\d{2}-\d{2}/.test(value)) {
          return moment(value).format('DD/MM/YYYY');
        }
        if (value.length > 60) return `${value.slice(0, 60)}…`;
        return value;
      }
      return String(value);
    },
    historyFieldLabel(field) {
      return HISTORY_FIELD_LABELS_VI[field] || field;
    },

    // ============== M6: Parts (Linh kiện + SP dịch vụ) ==============
    async loadParts() {
      if (!this.isEdit) return;
      try {
        const { data } = await ApiService.query(
          `${API_BASE}/${this.$route.params.id}/parts`,
        );
        this.parts = ((data && data.data) || []).map((p) => ({
          ...p,
          quantity: Number(p.quantity) || 1,
          price: Number(p.price) || 0,
          warrantyMonth: Number(p.warrantyMonth) || 0,
        }));
      } catch (err) {
        // Bỏ qua, table sẽ trống
      }
    },
    debouncedFetchParts: debounce(function (text) {
      this.fetchPartProducts(text);
    }, 350),
    onInputPart(text) {
      this.searchPartText = text || '';
      if (!text || text.trim().length < 2) {
        this.partSearchOptions = [];
        this.partToAdd = null;
        return;
      }
      this.partToAdd = null;
      this.debouncedFetchParts(text.trim());
    },
    async fetchPartProducts(text) {
      try {
        // Dùng chung endpoint với màn tạo hoá đơn để lấy tồn kho theo chi nhánh (quantityInStock = Tồn CTB).
        const { data } = await ApiService.query('productSearch', {
          params: {
            storeId: this.form.storeId,
            searchProduct: text,
          },
        });
        const all = (data && data.data) || [];
        // Giữ Linh kiện, Combo (vd gói linh kiện + phí sửa chữa) và SP dịch vụ.
        const ALLOWED = new Set([
          PRODUCT_TYPE.PRODUCT,
          PRODUCT_TYPE.PRODUCT_COMBO,
          PRODUCT_TYPE.PRODUCT_SERVICE,
        ]);
        this.partSearchOptions = all.filter((p) => ALLOWED.has(Number(p.productType)));
      } catch (err) {
        this.partSearchOptions = [];
      }
    },
    async onSelectPart(option) {
      const item = option && option.item;
      if (!item) return;
      // Chọn từ autosuggest → tự thêm vào danh sách (bỏ bước bấm "Thêm vào phiếu").
      this.partToAdd = item;
      this.searchPartText = item.productName;
      await this.confirmAddPart();
    },
    async confirmAddPart() {
      if (!this.partToAdd) return;
      this.addingPart = true;
      try {
        const payload = {
          productId: this.partToAdd.productId || this.partToAdd.id,
          quantity: 1,
          price: Number(this.partToAdd.sellingPrice) || 0,
        };
        await ApiService.post(
          `${API_BASE}/${this.$route.params.id}/parts`,
          payload,
        );
        makeToastSuccess(`Đã thêm "${this.partToAdd.productName}"`);
        this.searchPartText = '';
        this.partToAdd = null;
        this.partSearchOptions = [];
        await this.loadParts();
      } catch (err) {
        makeToastFaile(extractError(err));
      } finally {
        this.addingPart = false;
      }
    },
    async removePart(part) {
      const ok = await this.$bvModal.msgBoxConfirm(
        `Xoá "${part.productName}" khỏi phiếu?`,
        {
          title: 'Xác nhận xoá linh kiện',
          okTitle: 'Xoá',
          cancelTitle: 'Huỷ',
          okVariant: 'danger',
          centered: true,
        },
      );
      if (!ok) return;
      try {
        await ApiService.delete(`${API_BASE}/parts/${part.id}`);
        makeToastSuccess('Đã xoá linh kiện');
        await this.loadParts();
      } catch (err) {
        makeToastFaile(extractError(err));
      }
    },
    onPartChanged: debounce(async function (part) {
      // Auto-save khi user đổi qty/price/warrantyMonth (debounce 600ms tránh spam API).
      try {
        await ApiService.put(`${API_BASE}/parts/${part.id}`, {
          quantity: Number(part.quantity) || 1,
          price: Number(part.price) || 0,
          warrantyMonth: Number(part.warrantyMonth) || 0,
        });
      } catch (err) {
        makeToastFaile(extractError(err));
        await this.loadParts();
      }
    }, 600),
    computeLineTotal(part) {
      const qty = Number(part.quantity) || 0;
      const price = Number(part.price) || 0;
      return qty * price;
    },
    formatPrice(value) {
      return formatPrice(value);
    },
    partTypeLabel(type) {
      const t = Number(type);
      if (t === PRODUCT_TYPE.PRODUCT_SERVICE) return 'Dịch vụ';
      if (t === PRODUCT_TYPE.PRODUCT) return 'Linh kiện';
      if (t === PRODUCT_TYPE.PRODUCT_COMBO) return 'Combo';
      return String(type || '');
    },
    partTypeVariant(type) {
      const t = Number(type);
      if (t === PRODUCT_TYPE.PRODUCT_SERVICE) return 'info';
      if (t === PRODUCT_TYPE.PRODUCT) return 'warning';
      if (t === PRODUCT_TYPE.PRODUCT_COMBO) return 'success';
      return 'secondary';
    },

    /**
     * Jump tới step n (1-based, khớp KTWizard).
     * Dùng khi onSubmit fail validation cần đưa user về step có lỗi.
     */
    goToStep(n) {
      if (this.wizard && typeof this.wizard.goTo === 'function') {
        this.wizard.goTo(n);
      } else {
        this.currentStep = n;
      }
    },

    goBack() {
      this.$router.push({ name: 'list-repair-slips' });
    },

    /**
     * Click dropdown item HĐ BL / HĐ BH / HĐ Hoàn tiền → mở chi tiết Bill ở tab mới.
     * Dùng router.resolve để build URL — tự động đúng base path nếu deploy subdir.
     * @param {'service'|'warranty'|'refund'} kind
     */
    openLinkedBill(kind) {
      const map = {
        service: {
          num: this.form.serviceBillNumber,
          label: 'bán lẻ',
          route: { name: 'update-bill' },
          queryKey: 'id',
        },
        warranty: {
          num: this.form.warrantyBillNumber,
          label: 'bảo hành',
          route: { name: 'add-warranty-bill-v2' },
          queryKey: 'billId',
          extraQuery: { action: 'update' },
        },
        refund: {
          num: this.form.refundBillNumber,
          label: 'hoàn tiền',
          route: { name: 'update-bill' },
          queryKey: 'id',
        },
      };
      const target = map[kind];
      if (!target || !target.num) {
        makeToastFaile(`Không xác định được số hoá đơn ${target ? target.label : ''}.`);
        return;
      }
      const resolved = this.$router.resolve({
        ...target.route,
        query: { [target.queryKey]: target.num, ...(target.extraQuery || {}) },
      });
      window.open(resolved.href, '_blank');
    },

    // M9: In biên nhận + Hoá đơn DV — mở tab mới
    openPrintReceipt() {
      if (!this.form.id) return;
      const route = this.$router.resolve({
        name: 'print-repair-receipt',
        params: { id: this.form.id },
      });
      window.open(route.href, '_blank');
    },
    openPrintBill() {
      if (!this.form.id) return;
      const route = this.$router.resolve({
        name: 'print-repair-bill',
        params: { id: this.form.id },
      });
      window.open(route.href, '_blank');
    },
  },
};
</script>

<style lang="scss" scoped>
.repair-slip-form {
  ::v-deep .card-header {
    border-bottom: 1px solid #ebedf3;
  }

  // R12: chip nhân sự đã chọn (KTV chính/kiểm tra/điều phối)
  .tech-chip {
    font-size: 0.85rem;
    font-weight: 500;
  }

  // Phase 1 audit log — render snapshot (CREATE) + diff (UPDATE) trong cell Lịch sử.
  .history-snapshot,
  .history-diff {
    line-height: 1.5;
  }
  .history-diff-line {
    padding: 1px 0;
  }
  .history-field-label {
    font-weight: 600;
    margin-right: 4px;
  }
  .history-arrow {
    display: inline-block;
    margin: 0 6px;
    color: #3699ff;
    font-weight: 700;
  }

  // Match chiều cao Treeselect với b-form-input size=sm (~31px) để row địa chỉ thẳng hàng
  // với input "Địa chỉ" + "Mật khẩu". Default treeselect cao 36px → lệch nhìn rõ.
  .address-row {
    ::v-deep .vue-treeselect__control {
      height: 31px;
      min-height: 31px;
      padding: 0 6px;
      font-size: 0.875rem;
      border-color: #e4e6ef;
    }
    ::v-deep .vue-treeselect__single-value,
    ::v-deep .vue-treeselect__placeholder {
      line-height: 29px;
    }
    ::v-deep .vue-treeselect__input {
      height: 29px;
    }
    ::v-deep .vue-treeselect--disabled .vue-treeselect__control {
      background-color: #f3f6f9;
    }
  }

  .tech-chip-x {
    color: inherit;
    margin-left: 4px;
    font-weight: 700;
    text-decoration: none;
  }

  ::v-deep .table-vertical-center td {
    vertical-align: middle;
  }

  ::v-deep .wizard-nav-bg {
    background-color: #f7f7f7;
    border-radius: 5px 5px 0px 0px;
  }

  // Toolbar: badge + button cao đều, không wrap
  ::v-deep .toolbar-actions {
    .toolbar-badge {
      font-size: 0.78rem;
      font-weight: 600;
      padding: 0.45rem 0.65rem;
      white-space: nowrap;
    }
    .toolbar-badge.clickable {
      cursor: pointer;
      transition: opacity 0.15s ease;
    }
    .toolbar-badge.clickable:hover {
      opacity: 0.8;
      text-decoration: underline;
    }
    .btn,
    .dropdown {
      white-space: nowrap;
    }
  }

  label {
    font-weight: 500;
    font-size: 0.875rem;
    color: #3f4254;
    margin-bottom: 0.3rem;
  }

  // Banner "Đã ghi nhận yêu cầu hoàn tiền" — softer red, contrast cao.
  // Tránh dùng b-alert variant=danger vì bg/text cùng tông đỏ wash chữ phụ.
  .refund-banner {
    display: flex;
    gap: 0.75rem;
    padding: 0.75rem 1rem;
    background-color: #fff5f5;
    border: 1px solid #fecdd3;
    border-left: 4px solid #e11d48;
    border-radius: 6px;
    color: #3f4254;
  }

  .refund-banner__icon {
    flex: 0 0 auto;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background-color: #fecdd3;
    color: #be123c;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 0.95rem;
  }

  .refund-banner__content {
    flex: 1 1 auto;
    min-width: 0;
  }

  .refund-banner__title {
    color: #9f1239;
    font-size: 0.95rem;
    font-weight: 500;
    line-height: 1.4;

    strong {
      font-weight: 700;
      font-size: 1.05rem;
    }
  }

  .refund-banner__reason {
    color: #be123c;
    font-weight: 500;
  }

  .refund-banner__note {
    margin-top: 0.35rem;
    padding: 0.35rem 0.55rem;
    background-color: #fff;
    border: 1px solid #fecdd3;
    border-radius: 4px;
    color: #3f4254;
    font-size: 0.825rem;
    line-height: 1.4;
  }

  .refund-banner__hint {
    margin-top: 0.5rem;
    color: #5e6278;
    font-size: 0.8rem;
    line-height: 1.45;

    strong {
      color: #3f4254;
      font-weight: 600;
    }
  }
}
</style>

<!--
  Wizard-4 SCSS phải KHÔNG scoped để áp dụng cho .wizard-4 children.
  Workaround `.modal-backdrop` — bug Bootstrap-Vue / Metronic: backdrop computed opacity=1
  thay vì 0.5. Pattern này đã có trong AddV2.vue / Add.vue / TransferWarrantyStock.vue ...
-->
<style lang="scss">
@import '@/assets/sass/pages/wizard/wizard-4.scss';

.modal-backdrop {
  opacity: 0.5;
}
</style>
